import React, { useState, useEffect, useCallback, useMemo, createContext, useContext, Fragment } from "react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, AreaChart, Area } from "recharts";
import { Package, TrendingUp, TrendingDown, ArrowRightLeft, ShoppingCart, LayoutDashboard, Database, Upload, Settings, LogOut, ChevronRight, AlertTriangle, CheckCircle, Clock, Search, Filter, RefreshCw, Truck, Target, Zap, BarChart3, Users, Building2, Tag, Layers, Download, Activity, Scale, CalendarOff } from "lucide-react";

import {
  COLORS, apiFetch, inputStyle, labelStyle, btnPrimary, btnSecondary,
  btnSmallSuccess, btnSmallDanger, btnSmallInfo, thStyle, tdStyle, mono,
  cardStyle, tableWrap, downloadCSV, CSVButton, PageHeader, LoadingSpinner,
  EmptyState, MetricCard, StatusBadge, MonedaProvider, useMoneda,
  EVENTO_SESION_EXPIRADA, API_BASE,
} from "./shared/ui";
import { fechaLocalISO } from "./shared/fechas";
import { parseCSV } from "./shared/csv";
import { fmtMoneda, SIMBOLO_MONEDA_DEFAULT } from "./shared/moneda";
import KpisPage from "./bi/KpisPage";
import ReportBuilderPage from "./bi/ReportBuilderPage";
import SinMovimientoPage from "./bi/SinMovimientoPage";
import TrasladosPorSaldoPage from "./traslados/TrasladosPorSaldoPage";
import TrasladosMultiClasSaldoPage from "./traslados/TrasladosMultiClasSaldoPage";
import MultiClassifierFilter from "./shared/MultiClassifierFilter";
import TipoSelector from "./shared/TipoSelector";
// ═══════════════════════════════════════════════════════════════
// CLASSIFIER FILTER (reutilizable en todas las páginas)
// ═══════════════════════════════════════════════════════════════
function ClassifierFilter({ token, onChange, showBodega = false, showDias = false, compact = false }) {
  const [clasificadores, setClasificadores] = useState([]);
  const [selectedClas, setSelectedClas] = useState("");
  const [selectedTipo, setSelectedTipo] = useState("");
  const [selectedBodega, setSelectedBodega] = useState("");
  const [bodegas, setBodegas] = useState([]);
  const [dias, setDias] = useState(30);
  const [loaded, setLoaded] = useState(false);
  const clasSeleccionado = clasificadores.find(c => c.id_clasificador === selectedClas);

  useEffect(() => {
    apiFetch("/clasificadores", {}, token).then(res => {
      const list = Array.isArray(res) ? res : [];
      setClasificadores(list);
      setLoaded(true);
    }).catch(() => setLoaded(true));
    if (showBodega) {
      apiFetch("/bodegas?activo=true", {}, token).then(res => {
        setBodegas(Array.isArray(res) ? res : []);
      }).catch(() => {});
    }
  }, [token, showBodega]);

  useEffect(() => {
    setSelectedTipo("");
  }, [selectedClas]);

  useEffect(() => {
    if (onChange) onChange({ id_clasificador: selectedClas, id_tipo: selectedTipo, id_bodega: selectedBodega, dias });
  }, [selectedClas, selectedTipo, selectedBodega, dias, onChange]);

  if (!loaded) return null;

  return (
    <div style={{ display: "flex", gap: compact ? 8 : 16, alignItems: "flex-end", flexWrap: "wrap" }}>
      <div style={{ minWidth: compact ? 160 : 200 }}>
        <label style={labelStyle}><Tag size={12} style={{ marginRight: 4 }} />Clasificador</label>
        <select value={selectedClas} onChange={e => setSelectedClas(e.target.value)} style={inputStyle}>
          <option value="">— Todos (sin filtro) —</option>
          {clasificadores.map(c => (
            <option key={c.id_clasificador} value={c.id_clasificador}>
              {c.nombre_clasificador || c.id_clasificador}
            </option>
          ))}
        </select>
      </div>
      {(clasSeleccionado?.total_tipos > 0) && (
        <div style={{ minWidth: compact ? 140 : 180 }}>
          <label style={labelStyle}>Tipo</label>
          <TipoSelector key={selectedClas} token={token} clasificador={clasSeleccionado} value={selectedTipo} onChange={setSelectedTipo} />
        </div>
      )}
      {showBodega && (
        <div style={{ minWidth: compact ? 140 : 180 }}>
          <label style={labelStyle}>Bodega</label>
          <select value={selectedBodega} onChange={e => setSelectedBodega(e.target.value)} style={inputStyle}>
            <option value="">— Todas —</option>
            {bodegas.map(b => <option key={b.id_bodega} value={b.id_bodega}>{b.descripcion || b.id_bodega}</option>)}
          </select>
        </div>
      )}
      {showDias && (
        <div style={{ minWidth: 100 }}>
          <label style={labelStyle}>Período</label>
          <select value={dias} onChange={e => setDias(Number(e.target.value))} style={inputStyle}>
            <option value={7}>7 días</option><option value={30}>30 días</option>
            <option value={60}>60 días</option><option value={90}>90 días</option>
          </select>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// LOGIN
// ═══════════════════════════════════════════════════════════════
function LoginPage({ onLogin, aviso }) {
  const [view, setView] = useState("login"); // login | forgot | reset
  const [form, setForm] = useState({ username: "", password: "", id_empresa: "" });
  const [resetForm, setResetForm] = useState({ token: "", new_password: "", confirm: "" });
  const [error, setError] = useState(aviso || "");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setLoading(true); setError(""); setSuccess("");
    try { const data = await apiFetch("/auth/login", { method: "POST", body: JSON.stringify(form) }); onLogin(data); }
    catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  const handleForgot = async () => {
    if (!form.username || !form.id_empresa) { setError("Ingrese el ID Empresa y el Usuario"); return; }
    setLoading(true); setError(""); setSuccess("");
    try {
      await apiFetch("/auth/forgot-password", { method: "POST", body: JSON.stringify({ username: form.username, id_empresa: form.id_empresa }) });
      setSuccess("Si el usuario existe y tiene email registrado, recibirá instrucciones para restablecer su contraseña.");
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  const handleReset = async () => {
    if (!resetForm.token) { setError("Pegue el token que recibió por email"); return; }
    if (resetForm.new_password.length < 8) { setError("La contraseña debe tener al menos 8 caracteres"); return; }
    if (resetForm.new_password !== resetForm.confirm) { setError("Las contraseñas no coinciden"); return; }
    setLoading(true); setError(""); setSuccess("");
    try {
      await apiFetch("/auth/reset-password", { method: "POST", body: JSON.stringify({ token: resetForm.token, new_password: resetForm.new_password }) });
      setSuccess("Contraseña restablecida exitosamente. Ya puede iniciar sesión.");
      setResetForm({ token: "", new_password: "", confirm: "" });
      setTimeout(() => setView("login"), 3000);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  // Capturar token de URL (?token=xxx)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    if (t) { setResetForm(p => ({ ...p, token: t })); setView("reset"); window.history.replaceState({}, "", "/"); }
  }, []);

  const inpMono = { ...inputStyle, fontFamily: "'JetBrains Mono', monospace", fontSize: 13 };
  const tabs = [
    { id: "login",  label: "Iniciar sesión" },
    { id: "forgot", label: "Recuperar acceso" },
    { id: "reset",  label: "Restablecer" },
  ].filter(t => t.id !== "reset" || view === "reset");

  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      {/* Panel de marca */}
      <div style={{ width: 420, flexShrink: 0, background: COLORS.primary, display: "flex", flexDirection: "column", padding: "48px 44px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", width: 320, height: 320, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.1)", top: -80, right: -80, pointerEvents: "none" }} />
        <div style={{ position: "absolute", width: 200, height: 200, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.08)", bottom: 60, left: -60, pointerEvents: "none" }} />
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: "auto" }}>
          <div style={{ width: 40, height: 40, background: "rgba(255,255,255,0.15)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Package size={20} color="white" />
          </div>
          <span style={{ fontSize: 18, fontWeight: 700, color: "white", letterSpacing: "-0.3px" }}>InvÓptimo</span>
        </div>
        <div style={{ marginTop: "auto" }}>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: "white", lineHeight: 1.2, letterSpacing: "-0.5px", margin: "0 0 16px" }}>
            Inventario preciso.<br />
            <span style={{ color: "rgba(255,255,255,0.7)" }}>Decisiones en tiempo real.</span>
          </h1>
          <p style={{ fontSize: 14, color: "rgba(255,255,255,0.7)", lineHeight: 1.6, margin: "0 0 32px" }}>
            Motor ML que calcula pronósticos, detecta quiebres y sugiere traslados antes de que el problema llegue al cliente.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {["Pronóstico de demanda por bodega y clasificador", "Traslados sugeridos con score de prioridad ML", "Órdenes de compra generadas automáticamente", "Multi-empresa con roles y permisos granulares"].map((feat, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <div style={{ width: 20, height: 20, background: "rgba(255,255,255,0.15)", borderRadius: 5, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                  <CheckCircle size={11} color="white" />
                </div>
                <span style={{ fontSize: 13, color: "rgba(255,255,255,0.8)", lineHeight: 1.4 }}>{feat}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ marginTop: 48, fontSize: 11, color: "rgba(255,255,255,0.4)" }}>© 2026 InvÓptimo · Todos los derechos reservados</div>
      </div>

      {/* Panel de formulario */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "48px 32px", background: COLORS.bg }}>
        <div style={{ width: "100%", maxWidth: 380 }}>
          <div style={{ display: "flex", background: COLORS.surfaceHover, borderRadius: 8, padding: 3, marginBottom: 28 }}>
            {tabs.map(t => (
              <button key={t.id} onClick={() => { setView(t.id); setError(""); setSuccess(""); }}
                style={{ flex: 1, textAlign: "center", padding: "7px 8px", borderRadius: 6, border: "none", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: view === t.id ? 600 : 500, background: view === t.id ? COLORS.surface : "transparent", color: view === t.id ? COLORS.text : COLORS.textMuted, boxShadow: view === t.id ? "0 1px 3px rgba(0,0,0,0.08)" : "none" }}>
                {t.label}
              </button>
            ))}
          </div>

          <h2 style={{ fontSize: 22, fontWeight: 700, color: COLORS.text, letterSpacing: "-0.3px", margin: "0 0 6px" }}>
            {view === "login" ? "Bienvenido" : view === "forgot" ? "Recuperar acceso" : "Nueva contraseña"}
          </h2>
          <p style={{ fontSize: 14, color: COLORS.textMuted, margin: "0 0 28px" }}>
            {view === "login" && "Ingresa tus credenciales para continuar."}
            {view === "forgot" && "Ingresa tu ID de empresa y usuario para recibir instrucciones."}
            {view === "reset" && "Pega el token que recibiste por email e ingresa tu nueva contraseña."}
          </p>

          {error && (
            <div style={{ padding: "12px 14px", background: COLORS.dangerSoft, border: "1px solid #FECACA", borderRadius: 8, color: COLORS.danger, fontSize: 13, marginBottom: 20, display: "flex", alignItems: "center", gap: 8 }}>
              <AlertTriangle size={15} /> {error}
            </div>
          )}
          {success && (
            <div style={{ padding: "12px 14px", background: COLORS.successSoft, border: "1px solid #A7F3D0", borderRadius: 8, color: COLORS.success, fontSize: 13, marginBottom: 20 }}>
              {success}
            </div>
          )}

          {view === "login" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div><label style={labelStyle}>ID Empresa</label><input value={form.id_empresa} onChange={e => setForm(p => ({ ...p, id_empresa: e.target.value }))} placeholder="UUID de la empresa" style={inpMono} /></div>
              <div><label style={labelStyle}>Usuario</label><input value={form.username} onChange={e => setForm(p => ({ ...p, username: e.target.value }))} style={inputStyle} /></div>
              <div><label style={labelStyle}>Contraseña</label><input type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} style={inputStyle} onKeyDown={e => e.key === "Enter" && handleLogin()} /></div>
              <button onClick={handleLogin} disabled={loading} style={{ ...btnPrimary, marginTop: 4, width: "100%", justifyContent: "center", padding: "11px", opacity: loading ? 0.6 : 1 }}>{loading ? "Ingresando..." : "Ingresar"}</button>
            </div>
          )}

          {view === "forgot" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div><label style={labelStyle}>ID Empresa</label><input value={form.id_empresa} onChange={e => setForm(p => ({ ...p, id_empresa: e.target.value }))} placeholder="UUID de la empresa" style={inpMono} /></div>
              <div><label style={labelStyle}>Usuario</label><input value={form.username} onChange={e => setForm(p => ({ ...p, username: e.target.value }))} style={inputStyle} onKeyDown={e => e.key === "Enter" && handleForgot()} /></div>
              <button onClick={handleForgot} disabled={loading} style={{ ...btnPrimary, marginTop: 4, width: "100%", justifyContent: "center", padding: "11px", opacity: loading ? 0.6 : 1 }}>{loading ? "Enviando..." : "Enviar instrucciones"}</button>
            </div>
          )}

          {view === "reset" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div><label style={labelStyle}>Token</label><input value={resetForm.token} onChange={e => setResetForm(p => ({ ...p, token: e.target.value }))} placeholder="Pegar token del email" style={inpMono} /></div>
              <div><label style={labelStyle}>Nueva contraseña</label><input type="password" value={resetForm.new_password} onChange={e => setResetForm(p => ({ ...p, new_password: e.target.value }))} placeholder="Mínimo 8 caracteres" style={inputStyle} /></div>
              <div><label style={labelStyle}>Confirmar contraseña</label><input type="password" value={resetForm.confirm} onChange={e => setResetForm(p => ({ ...p, confirm: e.target.value }))} style={inputStyle} onKeyDown={e => e.key === "Enter" && handleReset()} /></div>
              <button onClick={handleReset} disabled={loading} style={{ ...btnPrimary, marginTop: 4, width: "100%", justifyContent: "center", padding: "11px", opacity: loading ? 0.6 : 1 }}>{loading ? "Restableciendo..." : "Restablecer contraseña"}</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SIDEBAR
// ═══════════════════════════════════════════════════════════════
const NAV_GROUPS = [
  {
    label: "Análisis",
    items: [
      { id: "dashboard",    icon: LayoutDashboard, label: "Dashboard" },
      { id: "rotacion",     icon: TrendingUp,      label: "Rotación" },
      { id: "pronosticos",  icon: BarChart3,        label: "Pronósticos" },
      { id: "calidad",      icon: Activity,         label: "Calidad Datos" },
    ],
  },
  {
    label: "Inteligencia de Negocios",
    items: [
      { id: "bi_kpis",     icon: Zap,        label: "KPIs" },
      { id: "bi_reportes", icon: Search,     label: "Reportes" },
      { id: "bi_sin_mov",  icon: CalendarOff, label: "Sin Movimiento" },
    ],
  },
  {
    label: "Operaciones",
    items: [
      { id: "inventario",      icon: Package,        label: "Inventario" },
      { id: "inventario_clas", icon: Filter,         label: "Inv. Clasificador" },
      { id: "traslados",       icon: ArrowRightLeft, label: "Traslados" },
      { id: "traslados_multi", icon: Layers,         label: "Traslados Multi." },
      { id: "traslados_multi_saldo", icon: Layers,   label: "Traslados Multi. +" },
      { id: "traslados_saldo", icon: Scale,          label: "Traslados Saldo" },
      { id: "compras",         icon: ShoppingCart,   label: "Ord. Compra" },
      { id: "upload",          icon: Upload,         label: "Carga Datos" },
    ],
  },
  {
    label: "Sistema",
    items: [
      { id: "maestros",       icon: Database, label: "Datos Maestros" },
      { id: "clasificadores", icon: Tag,      label: "Clasificadores" },
      { id: "config",         icon: Settings, label: "Configuración" },
    ],
  },
];

function Sidebar({ active, onNavigate, user, onLogout }) {
  return (
    <div style={{ width: 220, background: COLORS.surface, borderRight: `1px solid ${COLORS.border}`, display: "flex", flexDirection: "column", height: "100vh", position: "fixed", left: 0, top: 0, zIndex: 100, overflowY: "auto" }}>
      {/* Logo */}
      <div style={{ padding: "18px 16px 14px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 32, height: 32, background: COLORS.primary, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Package size={16} color="white" />
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, letterSpacing: "-0.3px" }}>InvÓptimo</div>
          <div style={{ fontSize: 10, color: COLORS.textDim, fontWeight: 500, marginTop: 1 }}>v1.0 · ML Engine</div>
        </div>
      </div>

      {/* Nav groups */}
      <div style={{ flex: 1, paddingBottom: 8 }}>
        {NAV_GROUPS.map(group => (
          <div key={group.label} style={{ padding: "14px 10px 4px" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.8px", padding: "0 6px", marginBottom: 4 }}>
              {group.label}
            </div>
            {group.items.map(item => {
              const Icon = item.icon;
              const isActive = active === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => onNavigate(item.id)}
                  style={{
                    width: "100%", display: "flex", alignItems: "center", gap: 9,
                    padding: "7px 10px", border: "none", borderRadius: 7,
                    cursor: "pointer", marginBottom: 1,
                    background: isActive ? COLORS.primarySoft : "transparent",
                    color: isActive ? COLORS.primary : COLORS.textMuted,
                    fontSize: 13, fontWeight: isActive ? 600 : 500,
                    fontFamily: "'DM Sans', sans-serif",
                    transition: "background 0.1s",
                  }}
                >
                  <Icon size={16} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* User footer */}
      <div style={{ padding: "12px 14px", borderTop: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 30, height: 30, background: COLORS.primarySoft, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary }}>{(user?.username || "U")[0].toUpperCase()}</span>
        </div>
        <div style={{ overflow: "hidden", flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.username}</div>
          <div style={{ fontSize: 10, color: COLORS.textDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.empresa_nombre}</div>
        </div>
        <button onClick={onLogout} style={{ background: "none", border: "none", color: COLORS.textDim, cursor: "pointer", padding: 4, borderRadius: 6, display: "flex", alignItems: "center" }}>
          <LogOut size={15} />
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// DASHBOARD
// ═══════════════════════════════════════════════════════════════
function DashboardPage({ token }) {
  const { simbolo } = useMoneda();
  const [data, setData] = useState(null);
  const [clasData, setClasData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clasLoading, setClasLoading] = useState(true);
  useEffect(() => {
    // Cargar KPIs primero — muestra el dashboard de inmediato
    apiFetch("/dashboard", {}, token)
      .catch(() => null)
      .then(d => { setData(d); setLoading(false); });
    // Cargar clasificadores en segundo plano sin bloquear el render inicial
    // (top-N ya agregado en SQL; el endpoint viejo devolvía 676K grupos)
    apiFetch("/dashboard/clasificadores", {}, token)
      .catch(() => null)
      .then(c => { setClasData(c); setClasLoading(false); });
  }, [token]);
  if (loading) return <LoadingSpinner />;
  if (!data) return <EmptyState message="No hay datos disponibles" />;
  const m = data.metricas;
  const ventasData = (data.ventas_7d || []).map(d => ({ ...d, fecha: d.fecha?.slice(5), total: Number(d.total) }));
  // El endpoint ya devuelve el top-8 agregado y ordenado por venta
  const clasChart = clasData?.data || [];
  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Vista general del inventario" action={
        <div style={{ display: "flex", gap: 8 }}>
          <CSVButton data={data.top_ganadores} filename="ganadores" label="Ganadores CSV" />
          <CSVButton data={data.top_perdedores} filename="perdedores" label="Perdedores CSV" />
        </div>
      } />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 28 }}>
        <MetricCard icon={Building2} label="Bodegas" value={m.total_bodegas} color={COLORS.primary} />
        <MetricCard icon={Package} label="SKUs Activos" value={m.total_sku_activos?.toLocaleString()} sub={`de ${m.total_articulos?.toLocaleString()} artículos`} color={COLORS.accent} />
        <MetricCard icon={Target} label="Valor Inventario" value={`${simbolo}${((m.valor_inventario || 0) / 1000).toFixed(0)}K`} color="#8b5cf6" />
        <MetricCard icon={Truck} label="Pendientes" value={m.traslados_pendientes || m.traslados_aprobados || 0} sub={`${m.traslados_sugeridos} sugeridos`} color={COLORS.warning} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 20, marginBottom: 24 }}>
        <div style={cardStyle}><h3 style={{ color: COLORS.text, fontSize: 16, fontWeight: 600, margin: "0 0 20px" }}>Ventas Últimos 7 Días</h3>
          <ResponsiveContainer width="100%" height={240}><AreaChart data={ventasData}><defs><linearGradient id="grad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.15} /><stop offset="95%" stopColor={COLORS.primary} stopOpacity={0.01} /></linearGradient></defs><CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} /><XAxis dataKey="fecha" stroke={COLORS.textDim} fontSize={12} /><YAxis stroke={COLORS.textDim} fontSize={12} /><Tooltip contentStyle={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text }} /><Area type="monotone" dataKey="total" stroke={COLORS.primary} fill="url(#grad)" strokeWidth={2} /></AreaChart></ResponsiveContainer>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div style={{ ...cardStyle, flex: 1 }}><h3 style={{ color: COLORS.text, fontSize: 14, fontWeight: 600, margin: "0 0 12px", display: "flex", alignItems: "center", gap: 6 }}><TrendingUp size={16} color={COLORS.ganador} /> Top Ganadores</h3>
            {(data.top_ganadores || []).map((g, i) => <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${COLORS.border}` }}><span style={{ color: COLORS.textMuted, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 150 }}>{g.descripcion || g.id_articulo}</span><span style={{ color: COLORS.ganador, fontWeight: 600, fontSize: 13, ...mono }}>{Number(g.total).toLocaleString()}</span></div>)}
          </div>
          <div style={{ ...cardStyle, flex: 1 }}><h3 style={{ color: COLORS.text, fontSize: 14, fontWeight: 600, margin: "0 0 12px", display: "flex", alignItems: "center", gap: 6 }}><TrendingDown size={16} color={COLORS.perdedor} /> Top Perdedores</h3>
            {(data.top_perdedores || []).map((g, i) => <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${COLORS.border}` }}><span style={{ color: COLORS.textMuted, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 150 }}>{g.descripcion || g.id_articulo}</span><span style={{ color: COLORS.perdedor, fontWeight: 600, fontSize: 13, ...mono }}>{Number(g.total).toLocaleString()}</span></div>)}
          </div>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <div style={cardStyle}><h3 style={{ color: COLORS.text, fontSize: 16, fontWeight: 600, margin: "0 0 16px", display: "flex", alignItems: "center", gap: 8 }}><Tag size={18} color={COLORS.accent} /> Ventas por Clasificador (30d)</h3>
            {clasLoading ? <div style={{ height: 240, display: "flex", alignItems: "center", justifyContent: "center", color: COLORS.textDim, fontSize: 13 }}>Cargando...</div> : clasChart.length === 0 ? <div style={{ height: 240, display: "flex", alignItems: "center", justifyContent: "center", color: COLORS.textDim, fontSize: 13 }}>Sin datos</div> : <ResponsiveContainer width="100%" height={240}><BarChart data={clasChart} layout="vertical" margin={{ left: 80 }}><CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} /><XAxis type="number" stroke={COLORS.textDim} fontSize={11} /><YAxis type="category" dataKey="nombre" stroke={COLORS.textDim} fontSize={10} width={80} /><Tooltip contentStyle={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text }} /><Bar dataKey="venta" fill={COLORS.primary} radius={[0, 4, 4, 0]} /></BarChart></ResponsiveContainer>}
          </div>
          <div style={cardStyle}><h3 style={{ color: COLORS.text, fontSize: 15, fontWeight: 600, margin: "0 0 14px" }}>Estado por Clasificador</h3>
            {clasLoading ? <div style={{ height: 240, display: "flex", alignItems: "center", justifyContent: "center", color: COLORS.textDim, fontSize: 13 }}>Cargando...</div> : <div style={{ maxHeight: 240, overflowY: "auto" }}>{clasChart.map((c, i) => <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 4px", borderBottom: `1px solid ${COLORS.border}` }}><span style={{ color: COLORS.textMuted, fontSize: 12, maxWidth: 170, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.nombre}</span><div style={{ display: "flex", gap: 12 }}><span style={{ color: COLORS.accent, fontSize: 12, ...mono }}>{c.venta?.toLocaleString()}</span><span style={{ color: COLORS.primary, fontSize: 12, ...mono }}>{c.stock?.toLocaleString()}</span>{c.criticos > 0 && <span style={{ color: COLORS.danger, fontSize: 11, fontWeight: 600 }}>{c.criticos}!</span>}</div></div>)}</div>}
          </div>
        </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// INVENTARIO — con filtro de clasificador
// ═══════════════════════════════════════════════════════════════
const INV_PAGE_SIZE = 200;
// acción del backend → contador del resumen, para paginar con el filtro puesto
const INV_ACCIONES = { CRITICO: "criticos", REABASTECER: "reabastecer", SOBREINVENTARIO: "sobreinventario", OK: "ok" };

function InventarioPage({ token }) {
  const [data, setData] = useState(null);
  const [clasFilter, setClasFilter] = useState({});
  const [accion, setAccion] = useState("CRITICO");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (clasFilter.id_clasificador) {
        const p = new URLSearchParams(); p.set("id_clasificador", clasFilter.id_clasificador);
        if (clasFilter.id_tipo) p.set("id_tipo", clasFilter.id_tipo);
        if (clasFilter.id_bodega) p.set("id_bodega", clasFilter.id_bodega);
        const res = await apiFetch(`/ml/classifier/inventory?${p}`, {}, token);
        setData({ resumen: res.resumen, data: res.data, modo: "clasificador" });
      } else {
        const p = new URLSearchParams({ page, size: INV_PAGE_SIZE });
        if (accion) p.set("accion", accion);
        if (clasFilter.id_bodega) p.set("id_bodega", clasFilter.id_bodega);
        const res = await apiFetch(`/ml/optimal-inventory?${p}`, {}, token);
        setData({ ...res, modo: "articulo" });
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, clasFilter, accion, page]);
  useEffect(() => { setPage(1); }, [clasFilter, accion]);
  useEffect(() => { load(); }, [load]);
  const summary = data?.resumen || {};
  const totalFiltrado = data?.modo === "articulo" ? (accion ? summary[INV_ACCIONES[accion]] : summary.total_skus) || 0 : 0;
  const paginas = Math.ceil(totalFiltrado / INV_PAGE_SIZE);
  return (
    <div>
      <PageHeader title="Inventario Óptimo" subtitle="Posición de inventario por artículo o clasificador" action={
        <div style={{ display: "flex", gap: 8 }}>
          <CSVButton data={data?.data} filename="inventario_optimo" />
          <button onClick={load} style={btnSecondary}><RefreshCw size={14} /> Recalcular</button>
        </div>
      } />
      <div style={{ ...cardStyle, marginBottom: 20, display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
        <ClassifierFilter token={token} onChange={setClasFilter} showBodega />
        {!clasFilter.id_clasificador && (
          <div style={{ minWidth: 170 }}>
            <label style={labelStyle}>Acción</label>
            <select value={accion} onChange={e => setAccion(e.target.value)} style={inputStyle}>
              <option value="">— Todas —</option>
              <option value="CRITICO">Crítico</option><option value="REABASTECER">Reabastecer</option>
              <option value="SOBREINVENTARIO">Sobreinventario</option><option value="OK">OK</option>
            </select>
          </div>
        )}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
        <MetricCard icon={Package} label="Total" value={summary.total_skus || summary.total_grupos || 0} color={COLORS.primary} />
        <MetricCard icon={AlertTriangle} label="Críticos" value={summary.criticos || 0} color={COLORS.danger} />
        <MetricCard icon={Clock} label="Reabastecer" value={summary.reabastecer || 0} color={COLORS.warning} />
        <MetricCard icon={CheckCircle} label="OK" value={summary.ok || 0} color={COLORS.success} />
      </div>
      <div style={tableWrap}><div style={{ maxHeight: 500, overflowY: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ background: COLORS.surfaceHover, position: "sticky", top: 0 }}>
            {(data?.modo === "clasificador" ? ["Tipo", "Bodega", "Artículos", "Stock", "Venta 30d", "Demanda/día", "Días Cob.", "Estado"] : ["Bodega", "Artículo", "Stock", "Demanda/día", "Días Cob.", "Pto. Reorden", "Óptimo", "Estado"]).map(h => <th key={h} style={thStyle}>{h}</th>)}
          </tr></thead>
          <tbody>{loading ? <tr><td colSpan={8} style={{ textAlign: "center", padding: 40, color: COLORS.textMuted }}>Cargando...</td></tr> :
            (data?.data || []).slice(0, 200).map((r, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                {data?.modo === "clasificador" ? (<>
                  <td style={tdStyle}>{r.nombre_tipo}</td><td style={tdStyle}>{r.bodega_desc || r.id_bodega}</td>
                  <td style={{ ...tdStyle, ...mono }}>{r.total_articulos}</td><td style={{ ...tdStyle, ...mono }}>{r.stock_total?.toLocaleString()}</td>
                  <td style={{ ...tdStyle, ...mono }}>{r.venta_30d?.toLocaleString()}</td><td style={{ ...tdStyle, ...mono }}>{r.demanda_diaria?.toFixed(1)}</td>
                  <td style={{ ...tdStyle, ...mono, color: r.dias_cobertura < 7 ? COLORS.danger : r.dias_cobertura < 15 ? COLORS.warning : COLORS.text }}>{r.dias_cobertura?.toFixed(0)}d</td>
                  <td style={tdStyle}><StatusBadge status={r.estado} /></td>
                </>) : (<>
                  <td style={tdStyle}>{r.id_bodega}</td><td style={tdStyle}>{r.id_articulo}</td>
                  <td style={{ ...tdStyle, ...mono }}>{r.stock_disponible?.toLocaleString()}</td><td style={{ ...tdStyle, ...mono }}>{r.demanda_diaria?.toFixed(1)}</td>
                  <td style={{ ...tdStyle, ...mono, color: r.dias_cobertura < 7 ? COLORS.danger : r.dias_cobertura < 15 ? COLORS.warning : COLORS.text }}>{r.dias_cobertura?.toFixed(0)}d</td>
                  <td style={{ ...tdStyle, ...mono }}>{r.punto_reorden}</td><td style={{ ...tdStyle, ...mono }}>{r.cantidad_optima}</td>
                  <td style={tdStyle}><StatusBadge status={r.accion} /></td>
                </>)}
              </tr>
            ))}</tbody>
        </table>
      </div></div>
      {data?.modo === "articulo" && paginas > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 4px 4px" }}>
          <span style={{ color: COLORS.textDim, fontSize: 13 }}>
            {totalFiltrado.toLocaleString()} SKU-bodega · página {page} de {paginas}
          </span>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => setPage(1)} disabled={loading || page === 1} style={{ ...btnSecondary, padding: "4px 10px", opacity: page === 1 ? 0.4 : 1 }}>«</button>
            <button onClick={() => setPage(p => p - 1)} disabled={loading || page === 1} style={{ ...btnSecondary, padding: "4px 10px", opacity: page === 1 ? 0.4 : 1 }}>‹</button>
            <button onClick={() => setPage(p => p + 1)} disabled={loading || page >= paginas} style={{ ...btnSecondary, padding: "4px 10px", opacity: page >= paginas ? 0.4 : 1 }}>›</button>
            <button onClick={() => setPage(paginas)} disabled={loading || page >= paginas} style={{ ...btnSecondary, padding: "4px 10px", opacity: page >= paginas ? 0.4 : 1 }}>»</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TRASLADOS — con detalle expandible y descarga ERP
// ═══════════════════════════════════════════════════════════════
function TrasladosPage({ token }) {
  const [traslados, setTraslados] = useState(null);
  const [clasFilter, setClasFilter] = useState({});
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [estadoFilter, setEstadoFilter] = useState("");
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState({});
  const [detalleCompleto, setDetalleCompleto] = useState({});
  const [csvLoading, setCsvLoading] = useState(false);
  const PAGE_SIZE = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, size: PAGE_SIZE });
      if (estadoFilter) params.set("estado", estadoFilter);
      if (clasFilter.id_clasificador) params.set("id_clasificador", clasFilter.id_clasificador);
      if (clasFilter.id_tipo) params.set("id_tipo", clasFilter.id_tipo);
      const res = await apiFetch(`/traslados?${params}`, {}, token);
      setTraslados(res); setDetalleCompleto({}); setExpanded({});
    }
    catch (e) { console.error(e); }
    setLoading(false);
  }, [token, estadoFilter, clasFilter, page]);
  useEffect(() => { setPage(1); }, [estadoFilter, clasFilter]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      if (clasFilter.id_clasificador) {
        const p = clasFilter.id_tipo ? `?id_tipo=${clasFilter.id_tipo}` : "";
        await apiFetch(`/ml/classifier/${clasFilter.id_clasificador}/suggest-transfers${p}`, { method: "POST" }, token);
      } else {
        await apiFetch("/ml/suggest-transfers", { method: "POST" }, token);
      }
      load();
    } catch (e) { alert(e.message); }
    setGenerating(false);
  };

  const handleAction = async (id, action) => {
    if (action === "cancelar" && !window.confirm("¿Cancelar este traslado? No se puede deshacer.")) return;
    try { await apiFetch(`/traslados/${id}/${action}`, { method: "PUT" }, token); load(); }
    catch (e) { alert(e.message); }
  };

  const toggleExpand = async (id) => {
    const isOpen = expanded[id];
    setExpanded(prev => ({ ...prev, [id]: !isOpen }));
    if (!isOpen && !detalleCompleto[id]) {
      try {
        const clasParam = clasFilter.id_clasificador ? `?id_clasificador=${clasFilter.id_clasificador}` : "";
        const dc = await apiFetch(`/traslados/${id}/detalle-completo${clasParam}`, {}, token);
        setDetalleCompleto(prev => ({ ...prev, [id]: dc }));
      } catch (e) { console.error(e); }
    }
  };

  const fetchAllAndDownload = async (tipo) => {
    setCsvLoading(tipo);
    try {
      const SIZE = 100;
      const buildCsvParams = (p) => {
        const params = new URLSearchParams({ page: p, size: SIZE });
        if (estadoFilter) params.set("estado", estadoFilter);
        if (clasFilter.id_clasificador) params.set("id_clasificador", clasFilter.id_clasificador);
        if (clasFilter.id_tipo) params.set("id_tipo", clasFilter.id_tipo);
        return params;
      };
      const firstRes = await apiFetch(`/traslados?${buildCsvParams(1)}`, {}, token);
      const total = Number(firstRes.total) || 0;
      let rows = firstRes.data || [];
      const totalPages = Math.ceil(total / SIZE);
      for (let p = 2; p <= totalPages; p++) {
        const res = await apiFetch(`/traslados?${buildCsvParams(p)}`, {}, token);
        rows = rows.concat(res.data || []);
      }
      if (rows.length === 0) {
        alert("No hay traslados para exportar con los filtros actuales.");
        setCsvLoading(false);
        return;
      }
      if (tipo === "resumen") {
        const data = rows.map(t => ({
          origen: t.origen_desc || t.id_bodega_origen,
          destino: t.destino_desc || t.id_bodega_destino,
          estado: t.estado,
          prioridad: t.score_prioridad,
          total_articulos: (t.detalle || []).length,
          fecha_sugerencia: t.fecha_sugerencia?.slice(0, 10) || "",
          fecha_aprobacion: t.fecha_aprobacion?.slice(0, 10) || "",
          fecha_recepcion: t.fecha_recepcion?.slice(0, 10) || "",
        }));
        downloadCSV(data, "traslados_resumen");
      } else {
        const detalle = [];
        rows.forEach(t => {
          (t.detalle || []).forEach(d => {
            detalle.push({
              id_traslado: t.id_traslado,
              bodega_origen: t.id_bodega_origen,
              origen_desc: t.origen_desc || "",
              bodega_destino: t.id_bodega_destino,
              destino_desc: t.destino_desc || "",
              estado: t.estado,
              id_articulo: d.id_articulo,
              ...(clasFilter.id_clasificador ? { tipo_clasificador: d.nombre_tipo || "" } : {}),
              cantidad: d.cantidad,
              razon: d.razon || "",
              prioridad: t.score_prioridad,
              fecha_sugerencia: t.fecha_sugerencia?.slice(0, 10) || "",
              fecha_aprobacion: t.fecha_aprobacion?.slice(0, 10) || "",
              fecha_recepcion: t.fecha_recepcion?.slice(0, 10) || "",
            });
          });
        });
        if (detalle.length === 0) {
          alert("No se encontraron artículos en los traslados. Verifique que los traslados tengan detalle cargado.");
          setCsvLoading(false);
          return;
        }
        downloadCSV(detalle, "traslados_detalle_erp");
      }
    } catch (e) {
      console.error(e);
      alert(`Error al generar CSV: ${e.message || "Error desconocido"}`);
    }
    setCsvLoading(false);
  };

  const btnRecibir = btnSmallInfo;
  const btnExpand = { padding: "4px 8px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontSize: 11, cursor: "pointer" };

  return (
    <div>
      <PageHeader title="Traslados entre Bodegas" subtitle="Sugerencias ML y gestión — haga clic en una fila para ver los artículos" action={
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => fetchAllAndDownload("resumen")} disabled={!!csvLoading} style={{ ...btnSmallSuccess, padding: "8px 14px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6, opacity: csvLoading ? 0.6 : 1 }}>
            <Download size={14} /> {csvLoading === "resumen" ? "Descargando..." : "Resumen CSV"}
          </button>
          <button onClick={() => fetchAllAndDownload("detalle")} disabled={!!csvLoading} style={{ ...btnSmallSuccess, padding: "8px 14px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6, opacity: csvLoading ? 0.6 : 1 }}>
            <Download size={14} /> {csvLoading === "detalle" ? "Descargando..." : "Detalle ERP CSV"}
          </button>
        </div>
      } />

      <div style={{ ...cardStyle, marginBottom: 20, display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
        <ClassifierFilter token={token} onChange={setClasFilter} compact />
        <select value={estadoFilter} onChange={e => setEstadoFilter(e.target.value)} style={{ ...inputStyle, width: 140 }}>
          <option value="">Todos</option><option value="sugerido">Sugeridos</option><option value="aprobado">Aprobados</option><option value="recibido">Recibidos</option><option value="recibido_por_tiempo">Recibidos (tiempo)</option>
        </select>
        <button onClick={generate} disabled={generating} style={btnPrimary}><Zap size={14} /> {generating ? "Generando..." : clasFilter.id_clasificador ? `Generar (${clasFilter.id_clasificador})` : "Generar Sugerencias ML"}</button>
      </div>

      <div style={tableWrap}><div style={{ maxHeight: 700, overflowY: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ background: COLORS.surfaceHover, position: "sticky", top: 0 }}>
            {["", "Origen", "Destino", "Estado", "Prioridad", "Artículos", "Fecha", "Acciones"].map(h => (
              <th key={h} style={{ ...thStyle, ...(h === "" ? { width: 36 } : {}) }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>{loading ? <tr><td colSpan={8} style={{ textAlign: "center", padding: 40, color: COLORS.textMuted }}>Cargando...</td></tr> :
            (traslados?.data || []).map((t, i) => {
              const est = (t.estado || "").toLowerCase().replace(/\s/g, "_");
              const isOpen = expanded[t.id_traslado];
              const detalle = t.detalle || [];
              return [
                <tr key={`h-${i}`} style={{ borderBottom: `1px solid ${COLORS.border}`, cursor: "pointer", background: isOpen ? `${COLORS.primary}08` : "transparent" }}
                    onClick={() => toggleExpand(t.id_traslado)}>
                  <td style={{ ...tdStyle, textAlign: "center", fontSize: 16, color: COLORS.textDim }}>{isOpen ? "▾" : "▸"}</td>
                  <td style={tdStyle}><div style={{ fontWeight: 600, color: COLORS.text }}>{t.origen_desc || t.id_bodega_origen}</div></td>
                  <td style={tdStyle}><div style={{ fontWeight: 600, color: COLORS.text }}>{t.destino_desc || t.id_bodega_destino}</div></td>
                  <td style={tdStyle}><StatusBadge status={t.estado} /></td>
                  <td style={{ ...tdStyle, ...mono, color: COLORS.accent }}>{t.score_prioridad?.toFixed(1)}</td>
                  <td style={{ ...tdStyle, ...mono }}>{detalle.length} items</td>
                  <td style={{ ...tdStyle, color: COLORS.textDim, fontSize: 12 }}>{t.fecha_sugerencia?.slice(0, 10)}</td>
                  <td style={tdStyle} onClick={e => e.stopPropagation()}><div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {est === "sugerido" && <>
                      <button onClick={() => handleAction(t.id_traslado, "aprobar")} style={btnSmallSuccess}>Aprobar</button>
                      <button onClick={() => handleAction(t.id_traslado, "cancelar")} style={btnSmallDanger}>Cancelar</button>
                    </>}
                    {est === "aprobado" && <>
                      <button onClick={() => handleAction(t.id_traslado, "recibir")} style={btnRecibir}>Recibir</button>
                      <button onClick={() => handleAction(t.id_traslado, "cancelar")} style={btnSmallDanger}>Cancelar</button>
                    </>}
                    {est === "recibido" && <span style={{ color: COLORS.success, fontSize: 11 }}>Completado</span>}
                    {est === "recibido_por_tiempo" && <span style={{ color: COLORS.warning, fontSize: 11 }}>Recibido (tiempo)</span>}
                  </div></td>
                </tr>,
                isOpen && (
                  <tr key={`d-${i}`}>
                    <td colSpan={8} style={{ padding: 0 }}>
                      <div style={{ background: `${COLORS.surfaceHover}`, borderBottom: `2px solid ${COLORS.primary}33`, padding: "12px 20px 12px 52px" }}>
                        {detalleCompleto[t.id_traslado] ? (() => {
                          const dc = detalleCompleto[t.id_traslado];
                          const allArts = (dc.tipos || []).flatMap(tipo => tipo.articulos.map(a => ({
                            tipo: tipo.nombre_tipo, ...a
                          })));
                          return <>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                              <span style={{ color: COLORS.textMuted, fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                                {dc.origen_desc} → {dc.destino_desc} — {dc.total_articulos_traslado} en traslado, {dc.total_articulos_disponibles} disponibles en {dc.total_tipos} tipos
                              </span>
                              <CSVButton data={allArts} filename={`traslado_completo_${t.id_bodega_origen}_a_${t.id_bodega_destino}`} label="Descargar completo" />
                            </div>
                            <div style={{ maxHeight: 500, overflowY: "auto" }}>
                              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead><tr>
                                  <th style={{ ...thStyle, padding: "6px 12px", background: `${COLORS.primary}12` }}>Artículo</th>
                                  <th style={{ ...thStyle, padding: "6px 12px", background: `${COLORS.primary}12` }}>Descripción</th>
                                  <th style={{ ...thStyle, padding: "6px 12px", background: `${COLORS.primary}12`, textAlign: "right" }}>Saldo</th>
                                  <th style={{ ...thStyle, padding: "6px 12px", background: `${COLORS.primary}12`, textAlign: "right" }}>En Tránsito</th>
                                  <th style={{ ...thStyle, padding: "6px 12px", background: `${COLORS.primary}12`, textAlign: "right" }}>Disponible</th>
                                  <th style={{ ...thStyle, padding: "6px 12px", background: `${COLORS.primary}12`, textAlign: "center" }}>Traslado</th>
                                  <th style={{ ...thStyle, padding: "6px 12px", background: `${COLORS.primary}12`, textAlign: "right" }}>Stock Destino</th>
                                  <th style={{ ...thStyle, padding: "6px 12px", background: `${COLORS.primary}12` }}>Razón</th>
                                </tr></thead>
                                <tbody>
                                  {(dc.tipos || []).map((tipo, ti) => (
                                    <React.Fragment key={ti}>
                                      <tr style={{ background: `${COLORS.accent}12` }}>
                                        <td colSpan={8} style={{ padding: "8px 12px", fontSize: 13, fontWeight: 700, color: COLORS.accent, borderBottom: `2px solid ${COLORS.accent}33` }}>
                                          {tipo.nombre_tipo || "Sin tipo"}
                                          <span style={{ fontWeight: 400, fontSize: 11, color: COLORS.textDim, marginLeft: 12 }}>
                                            {tipo.total_articulos} artículos · {tipo.en_traslado} en traslado · stock: {tipo.total_stock_origen?.toLocaleString()}
                                          </span>
                                        </td>
                                      </tr>
                                      {tipo.articulos.map((a, ai) => (
                                        <tr key={`${ti}-${ai}`} style={{ borderBottom: `1px solid ${COLORS.border}`, background: a.en_traslado ? `${COLORS.primary}08` : "transparent" }}>
                                          <td style={{ ...tdStyle, padding: "6px 12px", ...mono, fontSize: 12, fontWeight: a.en_traslado ? 600 : 400 }}>{a.id_articulo}</td>
                                          <td style={{ ...tdStyle, padding: "6px 12px", fontSize: 12, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.descripcion}</td>
                                          <td style={{ ...tdStyle, padding: "6px 12px", ...mono, textAlign: "right", color: COLORS.text }}>{a.saldo_origen?.toLocaleString()}</td>
                                          <td style={{ ...tdStyle, padding: "6px 12px", ...mono, textAlign: "right", color: a.en_transito_origen > 0 ? COLORS.warning : COLORS.textDim }}>{a.en_transito_origen > 0 ? a.en_transito_origen?.toLocaleString() : "—"}</td>
                                          <td style={{ ...tdStyle, padding: "6px 12px", ...mono, textAlign: "right", color: a.stock_origen <= 0 ? COLORS.danger : COLORS.text }}>{a.stock_origen?.toLocaleString()}</td>
                                          <td style={{ ...tdStyle, padding: "6px 12px", ...mono, textAlign: "center", fontWeight: 700, color: a.en_traslado ? COLORS.primary : COLORS.textDim }}>
                                            {a.cantidad_traslado > 0 ? <span style={{ padding: "2px 10px", background: `${COLORS.primary}18`, borderRadius: 6 }}>{a.cantidad_traslado}</span> : "—"}
                                          </td>
                                          <td style={{ ...tdStyle, padding: "6px 12px", ...mono, textAlign: "right", color: a.stock_destino <= 0 ? COLORS.danger : COLORS.text }}>{a.stock_destino?.toLocaleString()}</td>
                                          <td style={{ ...tdStyle, padding: "6px 12px" }}>
                                            {a.razon && <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 11, background: (a.razon || "").includes("alta") ? COLORS.successSoft : COLORS.warningSoft, color: (a.razon || "").includes("alta") ? COLORS.success : COLORS.warning }}>{(a.razon || "").replace(/_/g, " ")}</span>}
                                          </td>
                                        </tr>
                                      ))}
                                    </React.Fragment>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </>;
                        })() : (
                          <div style={{ textAlign: "center", padding: 20, color: COLORS.textDim }}>Cargando detalle completo...</div>
                        )}
                      </div>
                    </td>
                  </tr>
                ),
                isOpen && detalle.length === 0 && (
                  <tr key={`e-${i}`}>
                    <td colSpan={8} style={{ padding: "12px 52px", background: COLORS.surfaceHover, color: COLORS.textDim, fontSize: 13, borderBottom: `2px solid ${COLORS.primary}33` }}>
                      Sin artículos en este traslado
                    </td>
                  </tr>
                )
              ];
            })}</tbody>
        </table>
      </div></div>

      {/* Paginación */}
      {traslados && traslados.total > PAGE_SIZE && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 4px 4px" }}>
          <span style={{ color: COLORS.textDim, fontSize: 13 }}>
            {traslados.total} traslados · página {page} de {Math.ceil(traslados.total / PAGE_SIZE)}
          </span>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => setPage(1)} disabled={page === 1} style={{ ...btnSecondary, padding: "4px 10px", opacity: page === 1 ? 0.4 : 1 }}>«</button>
            <button onClick={() => setPage(p => p - 1)} disabled={page === 1} style={{ ...btnSecondary, padding: "4px 10px", opacity: page === 1 ? 0.4 : 1 }}>‹</button>
            <button onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(traslados.total / PAGE_SIZE)} style={{ ...btnSecondary, padding: "4px 10px", opacity: page >= Math.ceil(traslados.total / PAGE_SIZE) ? 0.4 : 1 }}>›</button>
            <button onClick={() => setPage(Math.ceil(traslados.total / PAGE_SIZE))} disabled={page >= Math.ceil(traslados.total / PAGE_SIZE)} style={{ ...btnSecondary, padding: "4px 10px", opacity: page >= Math.ceil(traslados.total / PAGE_SIZE) ? 0.4 : 1 }}>»</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TRASLADOS MULTI-CLASIFICADOR
// ═══════════════════════════════════════════════════════════════
function TrasladosMultiClasPage({ token }) {
  const [traslados, setTraslados] = useState(null);
  const [clasFilters, setClasFilters] = useState([]); // [{ id_clasificador, id_tipo }]
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [estadoFilter, setEstadoFilter] = useState("");
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState({});
  const [csvLoading, setCsvLoading] = useState(false);
  const PAGE_SIZE = 20;

  // Build mclas[] params from clasFilters
  const buildMclasParams = (params) => {
    clasFilters.forEach(f => {
      if (f.id_clasificador) {
        const val = f.id_tipo ? `${f.id_clasificador}:${f.id_tipo}` : f.id_clasificador;
        params.append("mclas", val);
      }
    });
    return params;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, size: PAGE_SIZE });
      if (estadoFilter) params.set("estado", estadoFilter);
      buildMclasParams(params);
      const res = await apiFetch(`/traslados?${params}`, {}, token);
      setTraslados(res); setExpanded({});
    }
    catch (e) { console.error(e); }
    setLoading(false);
  }, [token, estadoFilter, clasFilters, page]);

  useEffect(() => { setPage(1); }, [estadoFilter, clasFilters]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const activeClas = clasFilters.filter(f => f.id_clasificador);
      if (activeClas.length > 0) {
        const params = new URLSearchParams();
        activeClas.forEach(f => {
          params.append("mclas", f.id_tipo ? `${f.id_clasificador}:${f.id_tipo}` : f.id_clasificador);
        });
        await apiFetch(`/ml/suggest-transfers-by-classifiers?${params}`, { method: "POST" }, token);
      } else {
        await apiFetch("/ml/suggest-transfers", { method: "POST" }, token);
      }
      load();
    } catch (e) { alert(e.message); }
    setGenerating(false);
  };

  const handleAction = async (id, action) => {
    if (action === "cancelar" && !window.confirm("¿Cancelar este traslado? No se puede deshacer.")) return;
    try { await apiFetch(`/traslados/${id}/${action}`, { method: "PUT" }, token); load(); }
    catch (e) { alert(e.message); }
  };

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const fetchAllAndDownload = async (tipo) => {
    setCsvLoading(tipo);
    try {
      const SIZE = 100;
      const buildCsvParams = (p) => {
        const params = new URLSearchParams({ page: p, size: SIZE });
        if (estadoFilter) params.set("estado", estadoFilter);
        buildMclasParams(params);
        return params;
      };
      const firstRes = await apiFetch(`/traslados?${buildCsvParams(1)}`, {}, token);
      const total = Number(firstRes.total) || 0;
      let rows = firstRes.data || [];
      const totalPages = Math.ceil(total / SIZE);
      for (let p = 2; p <= totalPages; p++) {
        const res = await apiFetch(`/traslados?${buildCsvParams(p)}`, {}, token);
        rows = rows.concat(res.data || []);
      }
      if (rows.length === 0) {
        alert("No hay traslados para exportar con los filtros actuales.");
        setCsvLoading(false); return;
      }
      if (tipo === "resumen") {
        const data = rows.map(t => ({
          origen: t.origen_desc || t.id_bodega_origen,
          destino: t.destino_desc || t.id_bodega_destino,
          estado: t.estado, prioridad: t.score_prioridad,
          total_articulos: (t.detalle || []).length,
          fecha_sugerencia: t.fecha_sugerencia?.slice(0, 10) || "",
          fecha_aprobacion: t.fecha_aprobacion?.slice(0, 10) || "",
          fecha_recepcion: t.fecha_recepcion?.slice(0, 10) || "",
        }));
        downloadCSV(data, "traslados_multi_resumen");
      } else {
        const detalle = [];
        rows.forEach(t => {
          (t.detalle || []).forEach(d => {
            const row = {
              id_traslado: t.id_traslado,
              bodega_origen: t.id_bodega_origen, origen_desc: t.origen_desc || "",
              bodega_destino: t.id_bodega_destino, destino_desc: t.destino_desc || "",
              estado: t.estado, id_articulo: d.id_articulo,
              cantidad: d.cantidad, razon: d.razon || "",
              saldo_origen: d.saldo_origen ?? "",
              en_transito_origen: d.en_transito_origen ?? "",
              disponible_origen: d.stock_origen ?? "",
              saldo_destino: d.saldo_destino ?? "",
              prioridad: t.score_prioridad,
              fecha_sugerencia: t.fecha_sugerencia?.slice(0, 10) || "",
            };
            // Append tipo columns for each selected classifier
            clasFilters.forEach((f, i) => {
              if (f.id_clasificador) {
                row[`tipo_${f.id_clasificador}`] = d[`nombre_tipo_${i}`] || "";
              }
            });
            detalle.push(row);
          });
        });
        if (detalle.length === 0) {
          alert("No se encontraron artículos en los traslados.");
          setCsvLoading(false); return;
        }
        downloadCSV(detalle, "traslados_multi_detalle_erp");
      }
    } catch (e) {
      alert(`Error al generar CSV: ${e.message || "Error desconocido"}`);
    }
    setCsvLoading(false);
  };

  const btnRecibir = btnSmallInfo;

  // Build a label for active classifier filters
  const filterLabel = clasFilters.length > 0
    ? clasFilters.map(f => f.id_tipo ? `${f.id_clasificador}:${f.id_tipo}` : f.id_clasificador).join(" + ")
    : null;

  return (
    <div>
      <PageHeader
        title="Traslados — Multi Clasificador"
        subtitle="Filtre traslados por combinación de clasificadores (ej: Marca + Referencia)"
        action={
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => fetchAllAndDownload("resumen")} disabled={!!csvLoading} style={{ ...btnSmallSuccess, padding: "8px 14px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6, opacity: csvLoading ? 0.6 : 1 }}>
              <Download size={14} /> {csvLoading === "resumen" ? "Descargando..." : "Resumen CSV"}
            </button>
            <button onClick={() => fetchAllAndDownload("detalle")} disabled={!!csvLoading} style={{ ...btnSmallSuccess, padding: "8px 14px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6, opacity: csvLoading ? 0.6 : 1 }}>
              <Download size={14} /> {csvLoading === "detalle" ? "Descargando..." : "Detalle ERP CSV"}
            </button>
          </div>
        }
      />

      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>
          <MultiClassifierFilter token={token} onChange={setClasFilters} />
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div>
              <label style={labelStyle}>Estado</label>
              <select value={estadoFilter} onChange={e => setEstadoFilter(e.target.value)} style={{ ...inputStyle, width: 160 }}>
                <option value="">Todos</option>
                <option value="sugerido">Sugeridos</option>
                <option value="aprobado">Aprobados</option>
                <option value="recibido">Recibidos</option>
                <option value="recibido_por_tiempo">Recibidos (tiempo)</option>
              </select>
            </div>
            <button onClick={generate} disabled={generating} style={btnPrimary}>
              <Zap size={14} /> {generating ? "Generando..." : "Generar Sugerencias ML"}
            </button>
          </div>
        </div>
        {filterLabel && (
          <div style={{ marginTop: 12, padding: "8px 12px", background: `${COLORS.primary}12`, borderRadius: 8, fontSize: 12, color: COLORS.primary }}>
            Filtrando por: <strong>{filterLabel}</strong>
          </div>
        )}
      </div>

      <div style={tableWrap}><div style={{ maxHeight: 700, overflowY: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ background: COLORS.surfaceHover, position: "sticky", top: 0 }}>
            {["", "Origen", "Destino", "Estado", "Prioridad", "Artículos", "Fecha", "Acciones"].map(h => (
              <th key={h} style={{ ...thStyle, ...(h === "" ? { width: 36 } : {}) }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>{loading
            ? <tr><td colSpan={8} style={{ textAlign: "center", padding: 40, color: COLORS.textMuted }}>Cargando...</td></tr>
            : (traslados?.data || []).map((t, i) => {
              const est = (t.estado || "").toLowerCase().replace(/\s/g, "_");
              const isOpen = expanded[t.id_traslado];
              const detalle = t.detalle || [];
              return [
                <tr key={`h-${i}`}
                  style={{ borderBottom: `1px solid ${COLORS.border}`, cursor: "pointer", background: isOpen ? `${COLORS.primary}08` : "transparent" }}
                  onClick={() => toggleExpand(t.id_traslado)}>
                  <td style={{ ...tdStyle, textAlign: "center", fontSize: 16, color: COLORS.textDim }}>{isOpen ? "▾" : "▸"}</td>
                  <td style={tdStyle}><div style={{ fontWeight: 600, color: COLORS.text }}>{t.origen_desc || t.id_bodega_origen}</div></td>
                  <td style={tdStyle}><div style={{ fontWeight: 600, color: COLORS.text }}>{t.destino_desc || t.id_bodega_destino}</div></td>
                  <td style={tdStyle}><StatusBadge status={t.estado} /></td>
                  <td style={{ ...tdStyle, ...mono, color: COLORS.accent }}>{t.score_prioridad?.toFixed(1)}</td>
                  <td style={{ ...tdStyle, ...mono }}>{detalle.length} items</td>
                  <td style={{ ...tdStyle, color: COLORS.textDim, fontSize: 12 }}>{t.fecha_sugerencia?.slice(0, 10)}</td>
                  <td style={tdStyle} onClick={e => e.stopPropagation()}>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {est === "sugerido" && <>
                        <button onClick={() => handleAction(t.id_traslado, "aprobar")} style={btnSmallSuccess}>Aprobar</button>
                        <button onClick={() => handleAction(t.id_traslado, "cancelar")} style={btnSmallDanger}>Cancelar</button>
                      </>}
                      {est === "aprobado" && <>
                        <button onClick={() => handleAction(t.id_traslado, "recibir")} style={btnRecibir}>Recibir</button>
                        <button onClick={() => handleAction(t.id_traslado, "cancelar")} style={btnSmallDanger}>Cancelar</button>
                      </>}
                      {est === "recibido" && <span style={{ color: COLORS.success, fontSize: 11 }}>Completado</span>}
                      {est === "recibido_por_tiempo" && <span style={{ color: COLORS.warning, fontSize: 11 }}>Recibido (tiempo)</span>}
                    </div>
                  </td>
                </tr>,
                isOpen && (
                  <tr key={`d-${i}`}>
                    <td colSpan={8} style={{ padding: 0 }}>
                      <div style={{ background: COLORS.surfaceHover, borderBottom: `2px solid ${COLORS.primary}33`, padding: "12px 20px 12px 52px" }}>
                        {detalle.length > 0 ? (
                          <>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                              <span style={{ color: COLORS.textMuted, fontSize: 12 }}>
                                {t.origen_desc || t.id_bodega_origen} → {t.destino_desc || t.id_bodega_destino} — {detalle.length} artículo(s) que coinciden con el filtro
                              </span>
                              <CSVButton data={detalle.map(d => ({ id_articulo: d.id_articulo, cantidad: d.cantidad, saldo_origen: d.saldo_origen ?? "", saldo_destino: d.saldo_destino ?? "", razon: d.razon || "", ...Object.fromEntries(clasFilters.filter(f => f.id_clasificador).map((f, fi) => [f.id_clasificador, d[`nombre_tipo_${fi}`] || ""])) }))} filename={`traslado_${t.id_bodega_origen}_a_${t.id_bodega_destino}`} label="CSV" />
                            </div>
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                              <thead><tr style={{ background: `${COLORS.primary}12` }}>
                                <th style={{ ...thStyle, padding: "6px 12px" }}>Artículo</th>
                                {clasFilters.map((f, fi) => f.id_clasificador && (
                                  <th key={fi} style={{ ...thStyle, padding: "6px 12px" }}>{f.id_clasificador}</th>
                                ))}
                                <th style={{ ...thStyle, padding: "6px 12px" }}>Cantidad</th>
                                <th style={{ ...thStyle, padding: "6px 12px", textAlign: "right" }} title="Saldo actual del ERP en la bodega origen (antes del traslado)">Saldo Origen</th>
                                <th style={{ ...thStyle, padding: "6px 12px", textAlign: "right" }} title="Saldo actual del ERP en la bodega destino (antes del traslado)">Saldo Destino</th>
                                <th style={{ ...thStyle, padding: "6px 12px" }}>Razón</th>
                              </tr></thead>
                              <tbody>
                                {detalle.map((d, di) => (
                                  <tr key={di} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                                    <td style={{ ...tdStyle, padding: "6px 12px", ...mono, fontSize: 12 }}>{d.id_articulo}</td>
                                    {clasFilters.map((f, fi) => f.id_clasificador && (
                                      <td key={fi} style={{ ...tdStyle, padding: "6px 12px", fontSize: 11, color: COLORS.textMuted }}>
                                        {d[`nombre_tipo_${fi}`] || "—"}
                                      </td>
                                    ))}
                                    <td style={{ ...tdStyle, padding: "6px 12px", ...mono, textAlign: "center" }}>{d.cantidad}</td>
                                    <td style={{ ...tdStyle, padding: "6px 12px", ...mono, textAlign: "right", color: COLORS.text }}>{d.saldo_origen != null ? Number(d.saldo_origen).toLocaleString() : "—"}</td>
                                    <td style={{ ...tdStyle, padding: "6px 12px", ...mono, textAlign: "right", color: COLORS.text }}>{d.saldo_destino != null ? Number(d.saldo_destino).toLocaleString() : "—"}</td>
                                    <td style={{ ...tdStyle, padding: "6px 12px", fontSize: 11, color: COLORS.textDim }}>{d.razon || "—"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </>
                        ) : (
                          <span style={{ color: COLORS.textDim, fontSize: 13 }}>Sin artículos que coincidan con el filtro de clasificadores</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ),
              ];
            })
          }</tbody>
        </table>
      </div></div>

      {traslados && traslados.total > PAGE_SIZE && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 4px 4px" }}>
          <span style={{ color: COLORS.textDim, fontSize: 13 }}>
            {traslados.total} traslados · página {page} de {Math.ceil(traslados.total / PAGE_SIZE)}
          </span>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => setPage(1)} disabled={page === 1} style={{ ...btnSecondary, padding: "4px 10px", opacity: page === 1 ? 0.4 : 1 }}>«</button>
            <button onClick={() => setPage(p => p - 1)} disabled={page === 1} style={{ ...btnSecondary, padding: "4px 10px", opacity: page === 1 ? 0.4 : 1 }}>‹</button>
            <button onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(traslados.total / PAGE_SIZE)} style={{ ...btnSecondary, padding: "4px 10px", opacity: page >= Math.ceil(traslados.total / PAGE_SIZE) ? 0.4 : 1 }}>›</button>
            <button onClick={() => setPage(Math.ceil(traslados.total / PAGE_SIZE))} disabled={page >= Math.ceil(traslados.total / PAGE_SIZE)} style={{ ...btnSecondary, padding: "4px 10px", opacity: page >= Math.ceil(traslados.total / PAGE_SIZE) ? 0.4 : 1 }}>»</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// INVENTARIO POR MULTI-CLASIFICADOR
// ═══════════════════════════════════════════════════════════════
function InventarioClasPage({ token }) {
  const [clasFilters, setClasFilters] = useState([]);
  const [cantidadMax, setCantidadMax] = useState(10);
  const [detalleArticulo, setDetalleArticulo] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 100;

  const buildParams = (p) => {
    const params = new URLSearchParams({ cantidad_max: cantidadMax, page: p, size: PAGE_SIZE });
    clasFilters.forEach(f => {
      if (f.id_clasificador) {
        params.append("mclas", f.id_tipo ? `${f.id_clasificador}:${f.id_tipo}` : f.id_clasificador);
      }
    });
    return params;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`/inventario/multi-clas?${buildParams(page)}`, {}, token);
      setData(res);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, clasFilters, cantidadMax, page, detalleArticulo]);

  useEffect(() => { setPage(1); }, [clasFilters, cantidadMax, detalleArticulo]);
  useEffect(() => { load(); }, [load]);

  // Agrupar filas por bodega + combinación de clasificadores (una fila agregada por grupo)
  const grouped = React.useMemo(() => {
    if (!data?.data) return [];
    const activeClas = clasFilters.filter(f => f.id_clasificador);
    const map = {};
    data.data.forEach(row => {
      const bodega = row.bodega_desc || row.id_bodega || "";
      const clasValues = activeClas.map((f, i) => row[`nombre_tipo_${i}`] || "Sin clasificar");
      const key = [bodega, ...clasValues].join(" || ");
      if (!map[key]) map[key] = {
        key,
        desc: row.articulo_desc || "",
        bodega,
        clasValues,
        stock: 0,
        maxDiasIngreso: null,
      };
      map[key].stock += Number(row.stock_disponible) || 0;
      const dias = row.dias_desde_ingreso != null ? Number(row.dias_desde_ingreso) : null;
      if (dias != null && (map[key].maxDiasIngreso == null || dias > map[key].maxDiasIngreso)) {
        map[key].maxDiasIngreso = dias;
      }
    });
    return Object.values(map)
      .filter(g => g.stock <= cantidadMax)
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [data, clasFilters, cantidadMax]);

  const downloadAll = async () => {
    setCsvLoading(true);
    try {
      const first = await apiFetch(`/inventario/multi-clas?${buildParams(1)}`, {}, token);
      const total = Number(first.total) || 0;
      if (total === 0) { alert("No hay artículos para exportar con los filtros actuales."); setCsvLoading(false); return; }
      let rows = first.data || [];
      const pages = Math.ceil(total / PAGE_SIZE);
      for (let p = 2; p <= pages; p++) {
        const r = await apiFetch(`/inventario/multi-clas?${buildParams(p)}`, {}, token);
        rows = rows.concat(r.data || []);
      }
      const activeClas = clasFilters.filter(f => f.id_clasificador);
      const csv = rows.map(row => {
        const obj = { id_articulo: row.id_articulo, descripcion: row.articulo_desc, bodega: row.bodega_desc, stock: row.stock_disponible, fecha_ultimo_ingreso: row.fecha_ultimo_ingreso ?? "", dias_desde_ingreso: row.dias_desde_ingreso ?? "" };
        activeClas.forEach((f, i) => { obj[f.id_clasificador] = row[`nombre_tipo_${i}`] || ""; });
        return obj;
      });
      downloadCSV(csv, "inventario_por_clasificadores");
    } catch (e) { alert(`Error al exportar: ${e.message}`); }
    setCsvLoading(false);
  };

  const activeClas = clasFilters.filter(f => f.id_clasificador);
  const totalArticulos = data?.total || 0;
  const totalStock = (data?.data || []).reduce((s, r) => s + (Number(r.stock_disponible) || 0), 0);

  return (
    <div>
      <PageHeader
        title="Inventario por Clasificador"
        subtitle="Artículos agrupados por clasificadores con stock entre 1 y la cantidad máxima ingresada"
        action={
          <button onClick={downloadAll} disabled={csvLoading} style={{ ...btnSmallSuccess, padding: "8px 14px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6, cursor: csvLoading ? "wait" : "pointer", opacity: csvLoading ? 0.6 : 1 }}>
            <Download size={14} /> {csvLoading ? "Exportando..." : "Exportar CSV"}
          </button>
        }
      />

      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 32, alignItems: "flex-start", flexWrap: "wrap" }}>
          <MultiClassifierFilter token={token} onChange={setClasFilters} />
          <div>
            <label style={labelStyle}>Cantidad máxima de stock</label>
            <input
              type="number"
              min={0}
              value={cantidadMax}
              onChange={e => setCantidadMax(Number(e.target.value) || 0)}
              style={{ ...inputStyle, width: 140 }}
              placeholder="Ej: 10"
            />
            <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 4 }}>
              Muestra artículos con stock &gt; 0 y ≤ {cantidadMax}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", paddingBottom: 4 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}>
              <input
                type="checkbox"
                checked={detalleArticulo}
                onChange={e => setDetalleArticulo(e.target.checked)}
                style={{ width: 16, height: 16, accentColor: COLORS.primary, cursor: "pointer" }}
              />
              <span style={{ fontSize: 13, color: COLORS.text, fontWeight: 500 }}>Ver hasta nivel de artículo</span>
            </label>
            <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 4 }}>
              {detalleArticulo ? "Tabla plana con id_articulo visible" : "Agrupado por clasificadores"}
            </div>
          </div>
        </div>
      </div>

      {/* Métricas rápidas */}
      {data && (
        <div style={{ display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
          <MetricCard icon={Package} label="Artículos (página)" value={data.data?.length || 0} sub={`${totalArticulos} en total`} color={COLORS.primary} />
          {detalleArticulo
            ? <MetricCard icon={Layers} label="Grupos" value="—" sub="Vista por artículo activa" color={COLORS.accent} />
            : <MetricCard icon={Layers} label="Grupos" value={grouped.length} sub={activeClas.map(f => f.id_clasificador).join(" + ") || "Sin agrupar"} color={COLORS.accent} />
          }
          <MetricCard icon={TrendingDown} label="Stock total (página)" value={totalStock.toLocaleString()} sub="unidades" color={COLORS.warning} />
        </div>
      )}

      {loading
        ? <div style={{ ...cardStyle, textAlign: "center", padding: 40, color: COLORS.textMuted }}>Cargando...</div>
        : (!data?.data || data.data.length === 0)
          ? <div style={{ ...cardStyle, textAlign: "center", padding: 40, color: COLORS.textDim }}>No hay artículos con stock entre 1 y {cantidadMax} para los clasificadores seleccionados.</div>
          : detalleArticulo
            ? (
              <div style={cardStyle}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: COLORS.surfaceHover }}>
                      <th style={thStyle}>ID Artículo</th>
                      <th style={thStyle}>Descripción</th>
                      <th style={thStyle}>Bodega</th>
                      {activeClas.map((f, i) => (
                        <th key={i} style={thStyle}>{f.id_clasificador}</th>
                      ))}
                      <th style={{ ...thStyle, textAlign: "right" }}>Stock</th>
                      <th style={{ ...thStyle, textAlign: "right" }}>Días en bodega</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.data.map((row, ri) => (
                      <tr key={ri} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                        <td style={{ ...tdStyle, ...mono, fontSize: 12, color: COLORS.primary, fontWeight: 600 }}>{row.id_articulo}</td>
                        <td style={{ ...tdStyle, fontSize: 13 }}>{row.articulo_desc || "—"}</td>
                        <td style={{ ...tdStyle, fontSize: 12, color: COLORS.textMuted }}>{row.bodega_desc || row.id_bodega}</td>
                        {activeClas.map((f, i) => (
                          <td key={i} style={{ ...tdStyle, fontSize: 12, color: COLORS.textMuted }}>{row[`nombre_tipo_${i}`] || "—"}</td>
                        ))}
                        <td style={{ ...tdStyle, ...mono, textAlign: "right", fontWeight: 700, color: COLORS.accent }}>
                          {Number(row.stock_disponible).toLocaleString()}
                        </td>
                        <td style={{ ...tdStyle, ...mono, textAlign: "right", color: row.dias_desde_ingreso != null ? COLORS.text : COLORS.textDim }}>
                          {row.dias_desde_ingreso != null ? Number(row.dias_desde_ingreso).toLocaleString("es") : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
            : (
              <div style={cardStyle}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: COLORS.surfaceHover }}>
                      <th style={thStyle}>Descripción</th>
                      <th style={thStyle}>Bodega</th>
                      {activeClas.map((f, i) => (
                        <th key={i} style={thStyle}>{f.id_clasificador}</th>
                      ))}
                      <th style={{ ...thStyle, textAlign: "right" }}>Stock</th>
                      <th style={{ ...thStyle, textAlign: "right" }}>Máx. días en bodega</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped.map((group, gi) => (
                      <tr key={gi} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                        <td style={{ ...tdStyle, fontSize: 13 }}>{group.desc || "—"}</td>
                        <td style={{ ...tdStyle, fontSize: 12, color: COLORS.textMuted }}>{group.bodega || "—"}</td>
                        {group.clasValues.map((val, i) => (
                          <td key={i} style={{ ...tdStyle, fontSize: 12, color: COLORS.textMuted }}>{val}</td>
                        ))}
                        <td style={{ ...tdStyle, ...mono, textAlign: "right", fontWeight: 700, color: COLORS.accent }}>
                          {group.stock.toLocaleString()}
                        </td>
                        <td style={{ ...tdStyle, ...mono, textAlign: "right", color: group.maxDiasIngreso != null ? COLORS.text : COLORS.textDim }}>
                          {group.maxDiasIngreso != null ? group.maxDiasIngreso.toLocaleString("es") : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
      }

      {/* Paginación */}
      {data && data.total > PAGE_SIZE && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 4px 4px" }}>
          <span style={{ color: COLORS.textDim, fontSize: 13 }}>
            {data.total} artículos · página {page} de {Math.ceil(data.total / PAGE_SIZE)}
          </span>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => setPage(1)} disabled={page === 1} style={{ ...btnSecondary, padding: "4px 10px", opacity: page === 1 ? 0.4 : 1 }}>«</button>
            <button onClick={() => setPage(p => p - 1)} disabled={page === 1} style={{ ...btnSecondary, padding: "4px 10px", opacity: page === 1 ? 0.4 : 1 }}>‹</button>
            <button onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(data.total / PAGE_SIZE)} style={{ ...btnSecondary, padding: "4px 10px", opacity: page >= Math.ceil(data.total / PAGE_SIZE) ? 0.4 : 1 }}>›</button>
            <button onClick={() => setPage(Math.ceil(data.total / PAGE_SIZE))} disabled={page >= Math.ceil(data.total / PAGE_SIZE)} style={{ ...btnSecondary, padding: "4px 10px", opacity: page >= Math.ceil(data.total / PAGE_SIZE) ? 0.4 : 1 }}>»</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ROTACIÓN — con filtro de clasificador
// ═══════════════════════════════════════════════════════════════
const MAX_FILAS_ROTACION = 500;  // pintar 20k filas traba el navegador; el CSV lleva todo

function RotacionPage({ token }) {
  const [data, setData] = useState(null);
  const [clasFilter, setClasFilter] = useState({});
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (clasFilter.id_clasificador) {
        const p = new URLSearchParams({ dias: clasFilter.dias || 30 });
        if (clasFilter.id_tipo) p.set("id_tipo", clasFilter.id_tipo);
        if (clasFilter.id_bodega) p.set("id_bodega", clasFilter.id_bodega);
        const res = await apiFetch(`/ml/classifier/${clasFilter.id_clasificador}/rotation?${p}`, {}, token);
        setData({ ...res, modo: "clasificador" });
      } else {
        const p = new URLSearchParams({ nivel: "articulo", dias: clasFilter.dias || 30 });
        if (clasFilter.id_bodega) p.set("id_bodega", clasFilter.id_bodega);
        const res = await apiFetch(`/ml/rotation?${p}`, {}, token);
        setData({ ...res, modo: "articulo" });
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, clasFilter]);
  useEffect(() => { load(); }, [load]);
  const ganadores = data?.modo === "clasificador" ? (data?.ganadores || []) : (data?.data || []).filter(r => r.clasificacion === "ganador");
  const perdedores = data?.modo === "clasificador" ? (data?.perdedores || []) : (data?.data || []).filter(r => r.clasificacion === "perdedor");
  const resumen = data?.resumen || {};
  return (
    <div>
      <PageHeader title="Análisis de Rotación" subtitle="Ganadores y perdedores — filtre por clasificador para análisis detallado" action={
        <CSVButton data={data?.detalle || data?.data} filename="rotacion" />
      } />
      <div style={{ ...cardStyle, marginBottom: 20 }}><ClassifierFilter token={token} onChange={setClasFilter} showBodega showDias /></div>
      {data && <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
        <MetricCard icon={BarChart3} label="Total" value={resumen.total || resumen.total_items || 0} color={COLORS.primary} />
        <MetricCard icon={TrendingUp} label="Ganadores" value={resumen.ganadores || 0} color={COLORS.ganador} />
        <MetricCard icon={TrendingDown} label="Perdedores" value={resumen.perdedores || 0} color={COLORS.perdedor} />
        <MetricCard icon={Target} label="Venta Total" value={resumen.venta_total?.toLocaleString() || "—"} color={COLORS.accent} />
      </div>}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
        <div style={cardStyle}><h3 style={{ color: COLORS.ganador, fontSize: 15, fontWeight: 600, margin: "0 0 12px", display: "flex", alignItems: "center", gap: 6 }}><TrendingUp size={16} /> Top Ganadores</h3>
          {ganadores.slice(0, 10).map((g, i) => <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: `1px solid ${COLORS.border}` }}>
            <div><div style={{ color: COLORS.text, fontSize: 13 }}>{g.descripcion?.slice(0, 35) || g.id_referencia || g.id_articulo}</div>
              {(g.nombre_tipo || g.bodega_desc || g.id_bodega) && <div style={{ color: COLORS.textDim, fontSize: 11 }}>{[g.nombre_tipo, g.bodega_desc || g.id_bodega].filter(Boolean).join(" · ")}</div>}
            </div><span style={{ color: COLORS.ganador, fontWeight: 600, fontSize: 14, ...mono }}>{(g.total_vendido || 0).toLocaleString()}</span>
          </div>)}
          {ganadores.length === 0 && !loading && <EmptyState message="Sin datos" />}
        </div>
        <div style={cardStyle}><h3 style={{ color: COLORS.perdedor, fontSize: 15, fontWeight: 600, margin: "0 0 12px", display: "flex", alignItems: "center", gap: 6 }}><TrendingDown size={16} /> Top Perdedores</h3>
          {perdedores.slice(0, 10).map((g, i) => <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: `1px solid ${COLORS.border}` }}>
            <div><div style={{ color: COLORS.text, fontSize: 13 }}>{g.descripcion?.slice(0, 35) || g.id_referencia || g.id_articulo}</div>
              {(g.nombre_tipo || g.bodega_desc || g.id_bodega) && <div style={{ color: COLORS.textDim, fontSize: 11 }}>{[g.nombre_tipo, g.bodega_desc || g.id_bodega].filter(Boolean).join(" · ")}</div>}
            </div><span style={{ color: COLORS.perdedor, fontWeight: 600, fontSize: 14, ...mono }}>{(g.total_vendido || 0).toLocaleString()}</span>
          </div>)}
          {perdedores.length === 0 && !loading && <EmptyState message="Sin datos" />}
        </div>
      </div>
      {loading && <LoadingSpinner />}
      {/* Tabla detalle completa */}
      {(data?.detalle || data?.data || []).length > 0 && (
        <div style={tableWrap}><div style={{ maxHeight: 400, overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}><thead><tr style={{ background: COLORS.surfaceHover, position: "sticky", top: 0 }}>
            {["ID", "Descripción", data?.modo === "clasificador" ? "Tipo" : "Nivel", "Bodega", "Vendido", "Prom/día", "Clasif."].map(h => <th key={h} style={thStyle}>{h}</th>)}
          </tr></thead><tbody>
            {(data?.detalle || data?.data || []).slice(0, MAX_FILAS_ROTACION).map((r, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                <td style={{ ...tdStyle, ...mono, fontSize: 12 }}>{r.id_articulo || r.id_referencia}</td>
                <td style={{ ...tdStyle, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.descripcion}</td>
                <td style={tdStyle}>{r.nombre_tipo || r.nivel || ""}</td>
                <td style={tdStyle}>{r.bodega_desc || r.id_bodega || ""}</td>
                <td style={{ ...tdStyle, ...mono }}>{(r.total_vendido || 0).toLocaleString()}</td>
                <td style={{ ...tdStyle, ...mono }}>{(r.promedio_diario || 0).toFixed(1)}</td>
                <td style={tdStyle}><StatusBadge status={r.clasificacion} /></td>
              </tr>
            ))}
          </tbody></table>
        </div>
        {(data?.detalle || data?.data || []).length > MAX_FILAS_ROTACION && (
          <div style={{ color: COLORS.textDim, fontSize: 12, padding: "8px 4px" }}>
            Se muestran {MAX_FILAS_ROTACION} de {(data?.detalle || data?.data).length.toLocaleString()} filas; el CSV trae todas.
          </div>
        )}</div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// PRONÓSTICOS
// ═══════════════════════════════════════════════════════════════
// Sigue una corrida ML en background (GET /ml/runs/{id}) hasta que termine.
function useMlRun(token) {
  const [run, setRun] = useState(null);
  useEffect(() => {
    if (!run?.id_run || run.estado !== "corriendo") return;
    const t = setInterval(async () => {
      try {
        const r = await apiFetch(`/ml/runs/${run.id_run}`, {}, token);
        if (r.estado !== "corriendo") setRun(prev => ({ ...prev, ...r }));
      } catch (e) { setRun(prev => ({ ...prev, estado: "error", mensaje_error: e.message })); }
    }, 15000);
    return () => clearInterval(t);
  }, [run?.id_run, run?.estado, token]);
  return [run, setRun];
}

function MlRunEstado({ run }) {
  if (!run) return null;
  const color = run.estado === "error" ? COLORS.danger : run.estado === "completado" ? COLORS.success : COLORS.primary;
  const Icon = run.estado === "error" ? AlertTriangle : run.estado === "completado" ? CheckCircle : Clock;
  const texto = run.estado === "error" ? `Falló: ${run.mensaje_error || "error desconocido"}`
    : run.estado === "completado" ? `Completado: ${(run.registros_out ?? 0).toLocaleString()} pronósticos guardados.`
    : "Pronóstico en ejecución. Puede tardar hasta 25 minutos; puede salir de esta página y volver luego.";
  return (
    <div style={{ ...cardStyle, marginBottom: 20, display: "flex", alignItems: "center", gap: 10, color, fontSize: 14 }}>
      <Icon size={16} /> {texto}
    </div>
  );
}

function ErrorBox({ message }) {
  if (!message) return null;
  return <div style={{ padding: "12px 14px", background: COLORS.dangerSoft, border: "1px solid #FECACA", borderRadius: 8, color: COLORS.danger, fontSize: 13, marginBottom: 20, display: "flex", alignItems: "center", gap: 8 }}><AlertTriangle size={15} /> {message}</div>;
}

function PronosticosPage({ token }) {
  const [forecasts, setForecasts] = useState(null);
  const [bodega, setBodega] = useState(""); const [articulo, setArticulo] = useState(""); const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [run, setRun] = useMlRun(token);
  const corriendo = run?.estado === "corriendo";
  // Sin filtros el backend lo corre en background (toda la empresa tarda ~25 min)
  const generate = async () => {
    setLoading(true); setError(null); setForecasts(null); setRun(null);
    try {
      const p = new URLSearchParams(); if (bodega) p.set("id_bodega", bodega); if (articulo) p.set("id_articulo", articulo);
      const res = await apiFetch(`/ml/forecast?${p}`, { method: "POST" }, token);
      if (res.id_run) setRun(res); else setForecasts(res);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };
  // Totales por día de todas las series (data trae solo una muestra de 500 filas)
  const chartData = (forecasts?.por_fecha || []).map(d => ({ fecha: d.fecha_pronostico?.slice(5), demanda: d.demanda_estimada, lower: d.intervalo_inferior, upper: d.intervalo_superior }));
  return (
    <div>
      <PageHeader title="Pronóstico de Demanda" subtitle="Predicción ML con intervalos de confianza" action={
        <CSVButton data={forecasts?.data} filename="pronosticos" />
      } />
      <div style={{ ...cardStyle, marginBottom: 20 }}><div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
        <div><label style={labelStyle}>Bodega</label><input value={bodega} onChange={e => setBodega(e.target.value)} placeholder="ID Bodega" style={{ ...inputStyle, width: 160 }} /></div>
        <div><label style={labelStyle}>Artículo</label><input value={articulo} onChange={e => setArticulo(e.target.value)} placeholder="ID Artículo" style={{ ...inputStyle, width: 160 }} /></div>
        <button onClick={generate} disabled={loading || corriendo} style={btnPrimary}><Zap size={14} /> {loading ? "Calculando..." : "Generar Pronóstico"}</button>
      </div></div>
      <ErrorBox message={error} />
      <MlRunEstado run={run} />
      {forecasts && <div style={{ ...cardStyle, marginBottom: 20 }}><h3 style={{ color: COLORS.text, fontSize: 15, fontWeight: 600, margin: "0 0 16px" }}>Demanda total por día — {forecasts.horizon_dias} días, {forecasts.total_pronosticos?.toLocaleString()} predicciones</h3>
        <ResponsiveContainer width="100%" height={300}><AreaChart data={chartData}><CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} /><XAxis dataKey="fecha" stroke={COLORS.textDim} fontSize={11} /><YAxis stroke={COLORS.textDim} fontSize={11} /><Tooltip contentStyle={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text }} /><Area type="monotone" dataKey="upper" stroke="none" fill={`${COLORS.primary}22`} /><Area type="monotone" dataKey="lower" stroke="none" fill={COLORS.bg} /><Line type="monotone" dataKey="demanda" stroke={COLORS.primary} strokeWidth={2} dot={false} /></AreaChart></ResponsiveContainer>
      </div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// COMPRAS — con filtro de clasificador
// ═══════════════════════════════════════════════════════════════
function ComprasPage({ token }) {
  const PAGE_SIZE = 200;
  const [orders, setOrders] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const generate = useCallback(async () => {
    setLoading(true);
    try { setOrders(await apiFetch(`/ml/suggest-purchases?page=${page}&size=${PAGE_SIZE}`, {}, token)); }
    catch (e) { console.error(e); } setLoading(false);
  }, [token, page]);
  useEffect(() => { if (orders) generate(); }, [generate]);  // al cambiar de página; la 1ª carga es con "Regenerar"
  // El detalle por bodega no cabe en la tabla: va al CSV (blob, para llevar el Bearer).
  const descargar = async () => {
    setDescargando(true);
    try {
      const res = await fetch(`${API_BASE}/ml/suggest-purchases?formato=csv`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Error ${res.status} al generar el CSV`);
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url; a.download = `compras_sugeridas_${fechaLocalISO()}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert(e.message); }
    setDescargando(false);
  };
  const filas = orders?.data || [];
  const r = orders?.resumen;
  const paginas = Math.ceil((orders?.total || 0) / PAGE_SIZE);
  const num = { ...tdStyle, ...mono };
  return (
    <div>
      <PageHeader title="Órdenes de Compra Sugeridas" subtitle="Por artículo: lo que falta en las bodegas, menos lo que se puede trasladar y lo que ya viene en órdenes abiertas" action={
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={descargar} disabled={descargando} style={btnSecondary}><Download size={14} /> {descargando ? "Generando..." : "CSV por bodega"}</button>
          <button onClick={generate} disabled={loading} style={btnPrimary}><RefreshCw size={14} /> {loading ? "Calculando..." : "Regenerar"}</button>
        </div>
      } />
      {r && <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 20 }}>
        <MetricCard icon={AlertTriangle} label="Déficit en bodegas" value={r.deficit.toLocaleString()} sub={`${r.articulos_con_deficit.toLocaleString()} artículos`} color={COLORS.danger} />
        <MetricCard icon={ArrowRightLeft} label="Cubrible por traslado" value={r.por_traslado.toLocaleString()} sub="sobreinventario en otras bodegas" color={COLORS.primary} />
        <MetricCard icon={Truck} label="En camino" value={r.en_camino.toLocaleString()} sub="órdenes de compra abiertas" color={COLORS.warning} />
        <MetricCard icon={ShoppingCart} label="A comprar" value={r.a_comprar.toLocaleString()} sub={`${orders.total.toLocaleString()} artículos`} color={COLORS.accent} />
      </div>}
      <div style={tableWrap}><table style={{ width: "100%", borderCollapse: "collapse" }}><thead><tr style={{ background: COLORS.surfaceHover }}>
        {["Artículo", "Descripción", "Bodegas", "Déficit", "Por traslado", "En camino", "A comprar"].map(h => <th key={h} style={thStyle}>{h}</th>)}
      </tr></thead><tbody>
        {filas.map(f => <tr key={f.id_articulo} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
          <td style={{ ...tdStyle, ...mono, fontSize: 12 }}>{f.id_articulo}</td>
          <td style={{ ...tdStyle, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.descripcion}</td>
          <td style={num}>{f.bodegas}</td><td style={num}>{f.deficit.toLocaleString()}</td>
          <td style={num}>{f.por_traslado.toLocaleString()}</td><td style={num}>{f.en_camino.toLocaleString()}</td>
          <td style={{ ...num, fontWeight: 700, color: COLORS.accent }}>{f.a_comprar.toLocaleString()}</td>
        </tr>)}
        {filas.length === 0 && <tr><td colSpan={7} style={{ textAlign: "center", padding: 40, color: COLORS.textMuted }}>{loading ? "Calculando..." : orders ? "No hay nada que comprar" : "Presione Regenerar para calcular"}</td></tr>}
      </tbody></table></div>
      {paginas > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 4px 4px" }}>
          <span style={{ color: COLORS.textDim, fontSize: 13 }}>
            {orders.total.toLocaleString()} artículos · página {page} de {paginas}
          </span>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => setPage(1)} disabled={loading || page === 1} style={{ ...btnSecondary, padding: "4px 10px", opacity: page === 1 ? 0.4 : 1 }}>«</button>
            <button onClick={() => setPage(p => p - 1)} disabled={loading || page === 1} style={{ ...btnSecondary, padding: "4px 10px", opacity: page === 1 ? 0.4 : 1 }}>‹</button>
            <button onClick={() => setPage(p => p + 1)} disabled={loading || page >= paginas} style={{ ...btnSecondary, padding: "4px 10px", opacity: page >= paginas ? 0.4 : 1 }}>›</button>
            <button onClick={() => setPage(paginas)} disabled={loading || page >= paginas} style={{ ...btnSecondary, padding: "4px 10px", opacity: page >= paginas ? 0.4 : 1 }}>»</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CLASIFICADORES — página dedicada con 4 tabs
// ═══════════════════════════════════════════════════════════════
function PronosticoClasificadorTab({ token, clasificador, id_tipo, id_bodega }) {
  const [loading, setLoading] = useState(false);
  const [horizon, setHorizon] = useState(30);
  const [error, setError] = useState(null);
  const [run, setRun] = useMlRun(token);
  const corriendo = run?.estado === "corriendo";

  const runForecast = async () => {
    setLoading(true); setError(null); setRun(null);
    try {
      const tp = id_tipo ? `&id_tipo=${id_tipo}` : "";
      const bp = id_bodega ? `&id_bodega=${id_bodega}` : "";
      const data = await apiFetch(
        `/ml/classifier/${clasificador}/forecast?horizon=${horizon}${tp}${bp}`,
        { method: "POST" }, token
      );
      if (data.id_run) setRun(data); else setError(data.mensaje);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  return (
    <div>
      {/* Controles */}
      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div>
            <label style={labelStyle}>Horizonte (días)</label>
            <input type="number" value={horizon} onChange={e => setHorizon(Math.max(7, Math.min(90, parseInt(e.target.value) || 30)))} style={{ ...inputStyle, width: 80, margin: 0 }} />
          </div>
          <div style={{ flex: 1, paddingTop: 18 }}>
            <button onClick={runForecast} disabled={loading || corriendo} style={{ ...btnPrimary, opacity: loading || corriendo ? 0.6 : 1 }}>
              {loading ? "Iniciando..." : "Generar Pronóstico por Clasificador"}
            </button>
          </div>
        </div>
        <p style={{ color: COLORS.textDim, fontSize: 12, marginTop: 8, marginBottom: 0 }}>
          Pronostica cada artículo del clasificador en cada bodega con su propio historial de ventas (180 días) y guarda el resultado. Corre en segundo plano.
        </p>
      </div>

      <ErrorBox message={error} />

      {run && <>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 20 }}>
          <MetricCard icon={Package} label="Artículos" value={run.articulos?.toLocaleString()} color={COLORS.accent} />
          <MetricCard icon={BarChart3} label="Predicciones" value={run.estado === "completado" ? (run.registros_out ?? 0).toLocaleString() : "…"} color={COLORS.warning} />
          <MetricCard icon={Target} label="Horizonte" value={`${run.horizon_dias ?? horizon}d`} color={COLORS.text} />
        </div>
        <MlRunEstado run={run} />
      </>}

      {/* Estado inicial */}
      {!run && !loading && !error && (
        <div style={{ ...cardStyle, textAlign: "center", padding: 40 }}>
          <BarChart3 size={40} color={COLORS.textDim} style={{ marginBottom: 12 }} />
          <p style={{ color: COLORS.textMuted, fontSize: 14, margin: 0 }}>Presione "Generar Pronóstico" para pronosticar los artículos del clasificador</p>
        </div>
      )}
    </div>
  );
}

function TransferPairCard({ par, token, clasificador, id_tipo }) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [loadSize, setLoadSize] = useState(50);
  const loadDetail = async (pg = 1, sz = loadSize) => {
    setDetailLoading(true);
    try {
      const tp = id_tipo ? `&id_tipo=${id_tipo}` : "";
      const d = await apiFetch(`/ml/classifier/${clasificador}/transfer-view-detail?id_bodega_origen=${par.id_bodega_origen}&id_bodega_destino=${par.id_bodega_destino}&page=${pg}&size=${sz}${tp}`, {}, token);
      if (pg === 1) { setDetail(d); } else { setDetail(prev => ({ ...d, articulos: [...(prev?.articulos || []), ...d.articulos] })); }
      setPage(pg);
    } catch (e) { console.error(e); }
    setDetailLoading(false);
  };
  const handleExpand = () => { if (!expanded) { loadDetail(1, loadSize); } setExpanded(!expanded); };
  const allArticles = detail?.articulos || [];
  const tipoCounts = {};
  allArticles.forEach(a => {
    if (!tipoCounts[a.nombre_tipo]) tipoCounts[a.nombre_tipo] = { total: 0, conTraslado: 0 };
    tipoCounts[a.nombre_tipo].total++;
    if (a.sugerencia_traslado > 0) tipoCounts[a.nombre_tipo].conTraslado++;
  });
  return (
    <div style={{ ...cardStyle, marginBottom: 16 }}>
      <div onClick={handleExpand} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", padding: "4px 0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <ChevronRight size={18} color={COLORS.textDim} style={{ transform: expanded ? "rotate(90deg)" : "none", transition: "transform 0.2s" }} />
          <div style={{ padding: "6px 12px", background: COLORS.surfaceHover, borderRadius: 8, color: COLORS.text, fontSize: 14, fontWeight: 600 }}>{par.origen_desc}</div>
          <ArrowRightLeft size={18} color={COLORS.accent} />
          <div style={{ padding: "6px 12px", background: `${COLORS.accent}18`, borderRadius: 8, color: COLORS.accent, fontSize: 14, fontWeight: 600 }}>{par.destino_desc}</div>
        </div>
        <div style={{ display: "flex", gap: 20, fontSize: 12, color: COLORS.textDim, alignItems: "center" }}>
          <span><strong style={{ color: COLORS.warning }}>{par.articulos_con_traslado}</strong> con traslado de {par.total_articulos}</span>
          <span style={{ fontWeight: 600, color: COLORS.primary, fontSize: 14 }}>{par.total_trasladar?.toLocaleString()} unidades</span>
        </div>
      </div>
      {expanded && (
        <div style={{ marginTop: 16 }}>
          {detailLoading && !detail && <LoadingSpinner />}
          {detail && <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <span style={{ color: COLORS.textDim, fontSize: 12 }}>Mostrando {allArticles.length} de {detail.total} artículos (página {detail.page} de {detail.total_pages})</span>
              <CSVButton data={allArticles} filename={`traslado_${par.id_bodega_origen}_a_${par.id_bodega_destino}`} />
            </div>
            <div style={{ maxHeight: 500, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr style={{ background: COLORS.surfaceHover, position: "sticky", top: 0 }}>
                  {["Referencia", "Artículo", "Tipo", "Saldo", "En Tránsito", "Disponible", "Traslado", "Stock Destino"].map(h => <th key={h} style={thStyle}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {allArticles.map((a, ai) => {
                    const ht = a.sugerencia_traslado > 0;
                    const prevTipo = ai > 0 ? allArticles[ai - 1].nombre_tipo : null;
                    const showTipoHeader = a.nombre_tipo !== prevTipo;
                    const tc = tipoCounts[a.nombre_tipo] || { total: 0, conTraslado: 0 };
                    return <React.Fragment key={ai}>
                      {showTipoHeader && <tr style={{ background: `${COLORS.accent}12` }}>
                        <td colSpan={8} style={{ padding: "8px 12px", fontSize: 13, fontWeight: 700, color: COLORS.accent, borderBottom: `2px solid ${COLORS.accent}33` }}>
                          <span style={{ marginRight: 12 }}>{a.nombre_tipo}</span>
                          <span style={{ fontWeight: 400, fontSize: 11, color: COLORS.textDim }}>{tc.total} artículos{tc.conTraslado > 0 ? ` · ${tc.conTraslado} con traslado` : ""}</span>
                        </td>
                      </tr>}
                      <tr style={{ borderBottom: `1px solid ${COLORS.border}`, background: ht ? `${COLORS.primary}08` : "transparent" }}>
                      <td style={{ ...tdStyle, ...mono, fontSize: 12, fontWeight: ht ? 600 : 400 }}>{a.id_articulo}</td>
                      <td style={{ ...tdStyle, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.descripcion}</td>
                      <td style={{ ...tdStyle, fontSize: 12, color: COLORS.textDim }}>{a.nombre_tipo}</td>
                      <td style={{ ...tdStyle, ...mono, textAlign: "right", color: COLORS.text }}>{a.saldo_origen?.toLocaleString()}</td>
                      <td style={{ ...tdStyle, ...mono, textAlign: "right", color: a.en_transito_origen > 0 ? COLORS.warning : COLORS.textDim }}>{a.en_transito_origen > 0 ? a.en_transito_origen?.toLocaleString() : "\u2014"}</td>
                      <td style={{ ...tdStyle, ...mono, textAlign: "right", color: a.stock_origen <= 0 ? COLORS.danger : COLORS.text }}>{a.stock_origen?.toLocaleString()}</td>
                      <td style={{ ...tdStyle, ...mono, textAlign: "center", fontWeight: 700, color: ht ? COLORS.primary : COLORS.textDim, fontSize: ht ? 15 : 13 }}>
                        {ht ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 10px", background: `${COLORS.primary}18`, borderRadius: 6 }}>{a.sugerencia_traslado?.toLocaleString()} <ArrowRightLeft size={12} /></span> : "\u2014"}
                      </td>
                      <td style={{ ...tdStyle, ...mono, textAlign: "right", color: a.stock_destino <= 0 ? COLORS.danger : COLORS.text }}>{a.stock_destino?.toLocaleString()}</td>
                    </tr>
                    </React.Fragment>;
                  })}
                </tbody>
              </table>
            </div>
            {detail.page < detail.total_pages && (
              <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
                <button onClick={() => loadDetail(page + 1, loadSize)} disabled={detailLoading} style={{ ...btnPrimary, flex: 1, background: COLORS.surfaceHover, color: COLORS.textMuted, opacity: detailLoading ? 0.6 : 1 }}>
                  {detailLoading ? "Cargando..." : `Cargar más (${detail.total - allArticles.length} restantes)`}
                </button>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <input type="number" value={loadSize} onChange={e => { const v = Math.max(10, Math.min(5000, parseInt(e.target.value) || 50)); setLoadSize(v); }} style={{ ...inputStyle, width: 70, textAlign: "center", padding: "8px 6px", margin: 0 }} />
                  <span style={{ color: COLORS.textDim, fontSize: 11, whiteSpace: "nowrap" }}>por carga</span>
                </div>
              </div>
            )}
          </>}
        </div>
      )}
    </div>
  );
}

function ClasificadoresPage({ token }) {
  const [clasFilter, setClasFilter] = useState({});
  const [tab, setTab] = useState("inventario");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const loadData = useCallback(async () => {
    if (!clasFilter.id_clasificador) { setData(null); return; }
    setLoading(true);
    try {
      const tp = clasFilter.id_tipo ? `&id_tipo=${clasFilter.id_tipo}` : "";
      const bp = clasFilter.id_bodega ? `&id_bodega=${clasFilter.id_bodega}` : "";
      const d = clasFilter.dias || 30;
      if (tab === "inventario") setData(await apiFetch(`/ml/classifier/inventory?id_clasificador=${clasFilter.id_clasificador}${tp}${bp}`, {}, token));
      else if (tab === "rotacion") setData(await apiFetch(`/ml/classifier/${clasFilter.id_clasificador}/rotation?dias=${d}${tp}${bp}`, {}, token));
      // Generar reemplaza las sugerencias guardadas: solo con el botón, no al abrir la pestaña
      else if (tab === "traslados") { setData({ _sinGenerar: true }); }
      else if (tab === "vista_traslados") {
        const tp2 = clasFilter.id_tipo ? `?id_tipo=${clasFilter.id_tipo}` : "";
        setData(await apiFetch(`/ml/classifier/${clasFilter.id_clasificador}/transfer-view${tp2}`, {}, token));
      }
      else if (tab === "pronostico") { setData({ _ready: true }); }
      else if (tab === "reporte") setData(await apiFetch(`/ml/classifier/${clasFilter.id_clasificador}/report?dias=${d}`, {}, token));
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, clasFilter, tab]);
  useEffect(() => { loadData(); }, [loadData]);
  const generarTraslados = async () => {
    setLoading(true);
    try {
      const params = [];
      if (clasFilter.id_tipo) params.push(`id_tipo=${clasFilter.id_tipo}`);
      if (clasFilter.id_bodega) params.push(`id_bodega=${clasFilter.id_bodega}`);
      const qs = params.length > 0 ? `?${params.join("&")}` : "";
      setData(await apiFetch(`/ml/classifier/${clasFilter.id_clasificador}/suggest-transfers${qs}`, { method: "POST" }, token));
    } catch (e) { alert(e.message); }
    setLoading(false);
  };
  const tabs = [{ id: "inventario", icon: Package, label: "Inventario" }, { id: "rotacion", icon: TrendingUp, label: "Ganadores/Perdedores" }, { id: "traslados", icon: ArrowRightLeft, label: "Sugerencias Traslado" }, { id: "vista_traslados", icon: Truck, label: "Vista Completa Traslados" }, { id: "pronostico", icon: BarChart3, label: "Pronóstico" }, { id: "reporte", icon: Target, label: "Reporte Comparativo" }];
  return (
    <div>
      <PageHeader title="Análisis por Clasificador" subtitle="Seleccione un clasificador para ver inventario, rotación, traslados y reportes" action={
        <CSVButton data={data?.data || data?.detalle || data?.detalle_por_bodega} filename={`clasificador_${tab}`} />
      } />
      <div style={{ ...cardStyle, marginBottom: 20 }}><ClassifierFilter token={token} onChange={setClasFilter} showBodega showDias />
        {!clasFilter.id_clasificador && <div style={{ marginTop: 12, padding: "10px 14px", background: "#f59e0b12", border: "1px solid #f59e0b33", borderRadius: 8, color: COLORS.warning, fontSize: 13 }}>Seleccione un clasificador arriba para ver el análisis</div>}
      </div>
      {clasFilter.id_clasificador && <>
        <div style={{ display: "flex", gap: 4, marginBottom: 20, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 4 }}>
          {tabs.map(t => { const I = t.icon; return <button key={t.id} onClick={() => setTab(t.id)} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 12px", border: "none", borderRadius: 8, cursor: "pointer", background: tab === t.id ? COLORS.primarySoft : "transparent", color: tab === t.id ? COLORS.primary : COLORS.textMuted, fontSize: 13, fontWeight: tab === t.id ? 600 : 400 }}><I size={15} /> {t.label}</button>; })}
        </div>
        {loading ? <LoadingSpinner /> : !data ? <EmptyState message="Sin datos para este clasificador" /> : <>
          {tab === "inventario" && <div>
            {data.resumen && <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, marginBottom: 20 }}>
              <MetricCard icon={Layers} label="Grupos" value={data.resumen.total_grupos} color={COLORS.primary} /><MetricCard icon={AlertTriangle} label="Críticos" value={data.resumen.criticos} color={COLORS.danger} /><MetricCard icon={Clock} label="Reabastecer" value={data.resumen.reabastecer} color={COLORS.warning} /><MetricCard icon={CheckCircle} label="OK" value={data.resumen.ok} color={COLORS.success} /><MetricCard icon={Package} label="Stock" value={data.resumen.stock_total?.toLocaleString()} color={COLORS.accent} />
            </div>}
            <div style={tableWrap}><div style={{ maxHeight: 500, overflowY: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse" }}><thead><tr style={{ background: COLORS.surfaceHover, position: "sticky", top: 0 }}>{["Tipo", "Bodega", "Arts.", "Stock", "Venta 30d", "Dem/día", "Cob.", "Estado"].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead><tbody>
              {(data.data || []).map((r, i) => <tr key={i} style={{ borderBottom: `1px solid ${COLORS.border}` }}><td style={tdStyle}>{r.nombre_tipo}</td><td style={tdStyle}>{r.bodega_desc || r.id_bodega}</td><td style={{ ...tdStyle, ...mono }}>{r.total_articulos}</td><td style={{ ...tdStyle, ...mono }}>{r.stock_total?.toLocaleString()}</td><td style={{ ...tdStyle, ...mono }}>{r.venta_30d?.toLocaleString()}</td><td style={{ ...tdStyle, ...mono }}>{r.demanda_diaria?.toFixed(1)}</td><td style={{ ...tdStyle, ...mono, color: r.dias_cobertura < 7 ? COLORS.danger : r.dias_cobertura < 15 ? COLORS.warning : COLORS.text }}>{r.dias_cobertura?.toFixed(0)}d</td><td style={tdStyle}><StatusBadge status={r.estado} /></td></tr>)}
            </tbody></table></div></div>
          </div>}
          {tab === "rotacion" && <div>
            {data.resumen && <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}><MetricCard icon={BarChart3} label="Items" value={data.resumen.total_items} color={COLORS.primary} /><MetricCard icon={TrendingUp} label="Ganadores" value={data.resumen.ganadores} color={COLORS.ganador} /><MetricCard icon={TrendingDown} label="Perdedores" value={data.resumen.perdedores} color={COLORS.perdedor} /><MetricCard icon={Target} label="Venta Total" value={data.resumen.venta_total?.toLocaleString()} color={COLORS.accent} /></div>}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
              <div style={cardStyle}><h3 style={{ color: COLORS.ganador, fontSize: 15, fontWeight: 600, margin: "0 0 12px" }}>Top Ganadores</h3>{(data.ganadores || []).slice(0, 10).map((g, i) => <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${COLORS.border}` }}><div><div style={{ color: COLORS.text, fontSize: 13 }}>{g.descripcion?.slice(0, 30)}</div><div style={{ color: COLORS.textDim, fontSize: 11 }}>{g.nombre_tipo} · {g.bodega_desc || g.id_bodega}</div></div><div style={{ textAlign: "right" }}><div style={{ color: COLORS.ganador, fontWeight: 600, ...mono }}>{g.total_vendido?.toLocaleString()}</div><div style={{ color: COLORS.textDim, fontSize: 11 }}>P{g.percentil?.toFixed(0)}</div></div></div>)}</div>
              <div style={cardStyle}><h3 style={{ color: COLORS.perdedor, fontSize: 15, fontWeight: 600, margin: "0 0 12px" }}>Top Perdedores</h3>{(data.perdedores || []).slice(0, 10).map((g, i) => <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${COLORS.border}` }}><div><div style={{ color: COLORS.text, fontSize: 13 }}>{g.descripcion?.slice(0, 30)}</div><div style={{ color: COLORS.textDim, fontSize: 11 }}>{g.nombre_tipo} · {g.bodega_desc || g.id_bodega}</div></div><div style={{ textAlign: "right" }}><div style={{ color: COLORS.perdedor, fontWeight: 600, ...mono }}>{g.total_vendido?.toLocaleString()}</div><div style={{ color: COLORS.textDim, fontSize: 11 }}>P{g.percentil?.toFixed(0)}</div></div></div>)}</div>
            </div>
          </div>}
          {tab === "traslados" && data._sinGenerar && <div style={{ ...cardStyle, textAlign: "center", padding: 40 }}>
            <p style={{ color: COLORS.textMuted, fontSize: 14, margin: "0 0 16px" }}>Calcula las sugerencias de traslado de este clasificador y reemplaza las que se generaron antes para él.</p>
            <button onClick={generarTraslados} style={btnPrimary}><Zap size={14} /> Generar sugerencias</button>
          </div>}
          {tab === "traslados" && !data._sinGenerar && <div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12, marginBottom: 16 }}>
              <MetricCard icon={ArrowRightLeft} label="Sugerencias ML" value={data.total_sugerencias || 0} color={COLORS.primary} />
              <MetricCard icon={Layers} label="Total Artículos (incl. mismo tipo)" value={data.total_articulos || 0} color={COLORS.accent} />
            </div>
            <div style={{ ...tableWrap, marginTop: 8 }}><div style={{ maxHeight: 600, overflowY: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr style={{ background: COLORS.surfaceHover, position: "sticky", top: 0 }}>
                {["Artículo", "Descripción", "Origen", "Stock Origen", "Traslado", "Destino", "Razón", "Score"].map(h => <th key={h} style={thStyle}>{h}</th>)}
              </tr></thead>
              <tbody>
                {(() => {
                  const rows = data.data || [];
                  let lastTipo = null;
                  return rows.map((r, i) => {
                    const showHeader = r.nombre_tipo !== lastTipo;
                    lastTipo = r.nombre_tipo;
                    const isSuggested = r.en_sugerencia;
                    return <React.Fragment key={i}>
                      {showHeader && <tr style={{ background: `${COLORS.accent}12` }}>
                        <td colSpan={8} style={{ padding: "8px 12px", fontSize: 13, fontWeight: 700, color: COLORS.accent, borderBottom: `2px solid ${COLORS.accent}33` }}>
                          {r.nombre_tipo || "Sin tipo"}
                          <span style={{ fontWeight: 400, fontSize: 11, color: COLORS.textDim, marginLeft: 12 }}>
                            {rows.filter(x => x.nombre_tipo === r.nombre_tipo).length} artículos
                            {" · "}{rows.filter(x => x.nombre_tipo === r.nombre_tipo && x.en_sugerencia).length} sugeridos
                          </span>
                        </td>
                      </tr>}
                      <tr style={{ borderBottom: `1px solid ${COLORS.border}`, background: isSuggested ? `${COLORS.primary}08` : "transparent" }}>
                        <td style={{ ...tdStyle, ...mono, fontSize: 12, fontWeight: isSuggested ? 600 : 400 }}>{r.id_articulo}</td>
                        <td style={{ ...tdStyle, maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.descripcion_articulo}</td>
                        <td style={{ ...tdStyle, fontWeight: 600, color: COLORS.text }}>{r.bodega_origen}</td>
                        <td style={{ ...tdStyle, ...mono, textAlign: "right" }}>{r.stock_actual_origen != null ? r.stock_actual_origen?.toLocaleString() : "—"}</td>
                        <td style={{ ...tdStyle, ...mono, textAlign: "center", fontWeight: 700, color: isSuggested ? COLORS.primary : COLORS.textDim, fontSize: isSuggested ? 15 : 13 }}>
                          {r.cantidad_sugerida > 0 ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 10px", background: `${COLORS.primary}18`, borderRadius: 6 }}>{r.cantidad_sugerida} <ArrowRightLeft size={12} /></span> : "\u2014"}
                        </td>
                        <td style={{ ...tdStyle, fontWeight: 600, color: COLORS.accent }}>{r.bodega_destino}</td>
                        <td style={tdStyle}>{r.razon ? <span style={{ padding: "2px 8px", borderRadius: 4, background: r.razon?.includes("alta") ? COLORS.successSoft : COLORS.warningSoft, color: r.razon?.includes("alta") ? COLORS.success : COLORS.warning, fontSize: 11 }}>{r.razon?.replace(/_/g, " ")}</span> : ""}</td>
                        <td style={{ ...tdStyle, ...mono, color: COLORS.accent }}>{r.score > 0 ? r.score?.toFixed(1) : ""}</td>
                      </tr>
                    </React.Fragment>;
                  });
                })()}
                {(data.data || []).length === 0 && <tr><td colSpan={8} style={{ textAlign: "center", padding: 40, color: COLORS.textDim }}>Sin sugerencias para este clasificador</td></tr>}
              </tbody>
            </table></div></div>
          </div>}
          {tab === "vista_traslados" && <div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 20 }}>
              <MetricCard icon={Layers} label="Artículos" value={data.total_articulos || 0} color={COLORS.primary} />
              <MetricCard icon={ArrowRightLeft} label="Pares Bodega" value={data.total_pares || 0} color={COLORS.accent} />
              <MetricCard icon={Truck} label="Con Traslado" value={(data.pares || []).reduce((s, p) => s + p.articulos_con_traslado, 0)} color={COLORS.warning} />
            </div>
            {(data.pares || []).length === 0 && <EmptyState message="Sin traslados activos para este clasificador. Genere sugerencias primero desde la pestaña 'Sugerencias Traslado'." />}
            {(data.pares || []).map((par, pi) => <TransferPairCard key={`${par.id_bodega_origen}-${par.id_bodega_destino}`} par={par} token={token} clasificador={clasFilter.id_clasificador} id_tipo={clasFilter.id_tipo} />)}
          </div>}
          {tab === "pronostico" && <PronosticoClasificadorTab token={token} clasificador={clasFilter.id_clasificador} id_tipo={clasFilter.id_tipo} id_bodega={clasFilter.id_bodega} />}
          {tab === "reporte" && <div>
            {data.resumen_por_tipo && <><div style={{ ...cardStyle, marginBottom: 20 }}><h3 style={{ color: COLORS.text, fontSize: 15, fontWeight: 600, margin: "0 0 16px" }}>Ventas por Tipo</h3>
              <ResponsiveContainer width="100%" height={250}><BarChart data={(data.resumen_por_tipo || []).slice(0, 12).map(t => ({ nombre: t.nombre_tipo?.slice(0, 15), vendido: t.total_vendido, stock: t.stock_total }))}><CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} /><XAxis dataKey="nombre" stroke={COLORS.textDim} fontSize={11} /><YAxis stroke={COLORS.textDim} fontSize={11} /><Tooltip contentStyle={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text }} /><Bar dataKey="vendido" name="Vendido" fill={COLORS.accent} radius={[4, 4, 0, 0]} /><Bar dataKey="stock" name="Stock" fill={COLORS.primary} radius={[4, 4, 0, 0]} /><Legend formatter={v => <span style={{ color: COLORS.textMuted, fontSize: 12 }}>{v}</span>} /></BarChart></ResponsiveContainer>
            </div></>}
            <div style={tableWrap}><div style={{ maxHeight: 400, overflowY: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse" }}><thead><tr style={{ background: COLORS.surfaceHover, position: "sticky", top: 0 }}>{["Tipo", "Bodega", "Arts.", "Vendido", "Prom/día", "Stock", "Cob."].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead><tbody>
              {(data.detalle_por_bodega || []).map((r, i) => <tr key={i} style={{ borderBottom: `1px solid ${COLORS.border}` }}><td style={tdStyle}>{r.nombre_tipo}</td><td style={tdStyle}>{r.bodega_desc || r.id_bodega}</td><td style={{ ...tdStyle, ...mono }}>{r.total_articulos}</td><td style={{ ...tdStyle, ...mono, fontWeight: 600, color: COLORS.accent }}>{r.total_vendido?.toLocaleString()}</td><td style={{ ...tdStyle, ...mono }}>{r.promedio_diario?.toFixed(1)}</td><td style={{ ...tdStyle, ...mono }}>{r.stock_total?.toLocaleString()}</td><td style={{ ...tdStyle, ...mono, color: r.dias_cobertura < 7 ? COLORS.danger : r.dias_cobertura < 15 ? COLORS.warning : COLORS.text }}>{r.dias_cobertura?.toFixed(0)}d</td></tr>)}
            </tbody></table></div></div>
          </div>}
        </>}
      </>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// UPLOAD
// ═══════════════════════════════════════════════════════════════
function UploadPage({ token }) {
  const [tipo, setTipo] = useState("inventario"); const [file, setFile] = useState(null); const [progress, setProgress] = useState(null); const [log, setLog] = useState([]);
  const processUpload = async () => {
    if (!file) return; setLog([]); setProgress({ status: "reading" });
    try {
      const { records } = parseCSV(await file.text());
      const BS = 300, tb = Math.ceil(records.length / BS);
      for (let b = 0; b < tb; b++) {
        setProgress({ block: b + 1, total: tb, status: "uploading" });
        const res = await apiFetch("/data/upload", { method: "POST", body: JSON.stringify({ header: { tipo_dato: tipo, bloque_actual: b + 1, total_bloques: tb, total_registros: records.length }, registros: records.slice(b * BS, (b + 1) * BS) }) }, token);
        setLog(prev => [...prev, { block: b + 1, ok: res.registros_ok, errors: res.registros_error, estado: res.estado }]);
        if (res.estado === "error_reenviar") { setProgress({ status: "error", message: res.mensaje }); return; }
      }
      setProgress({ status: "done", total: records.length });
    } catch (e) { setProgress({ status: "error", message: e.message }); }
  };
  return (
    <div><PageHeader title="Carga de Datos" subtitle="Importar datos en bloques de 300 registros (CSV)" />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div style={cardStyle}><h3 style={{ color: COLORS.text, fontSize: 16, fontWeight: 600, margin: "0 0 20px" }}>Cargar CSV</h3>
          <div style={{ marginBottom: 16 }}><label style={labelStyle}>Tipo de Datos</label><select value={tipo} onChange={e => setTipo(e.target.value)} style={inputStyle}>
            <option value="inventario">Inventario</option><option value="ventas">Ventas</option><option value="ingresos">Ingresos</option><option value="articulos">Artículos</option><option value="bodegas">Bodegas</option>
            <option value="clasificadores">Clasificadores</option><option value="tipos_clasificador">Tipos Clasificador</option><option value="articulo_clasificadores">Artículo-Clasificadores</option>
          </select></div>
          <div style={{ marginBottom: 16 }}><label style={labelStyle}>Archivo CSV</label><input type="file" accept=".csv" onChange={e => setFile(e.target.files[0])} style={{ ...inputStyle, padding: 10 }} /></div>
          <button onClick={processUpload} disabled={!file || progress?.status === "uploading"} style={btnPrimary}><Upload size={14} /> {progress?.status === "uploading" ? `Bloque ${progress.block}/${progress.total}...` : "Iniciar Carga"}</button>
          {progress?.status === "done" && <div style={{ marginTop: 16, padding: "12px 14px", background: COLORS.successSoft, border: "1px solid #A7F3D0", borderRadius: 8, color: COLORS.success, fontSize: 13 }}>Completado: {progress.total} registros</div>}
          {progress?.status === "error" && <div style={{ marginTop: 16, padding: "12px 14px", background: COLORS.dangerSoft, border: "1px solid #FECACA", borderRadius: 8, color: COLORS.danger, fontSize: 13 }}>Error: {progress.message}</div>}
        </div>
        <div style={cardStyle}><h3 style={{ color: COLORS.text, fontSize: 16, fontWeight: 600, margin: "0 0 20px" }}>Log de Carga</h3>
          <div style={{ maxHeight: 300, overflowY: "auto" }}>{log.length === 0 ? <p style={{ color: COLORS.textDim, fontSize: 14 }}>Inicie una carga...</p> : log.map((l, i) => <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 12px", marginBottom: 4, borderRadius: 8, background: l.errors > 0 ? COLORS.dangerSoft : COLORS.successSoft }}><span style={{ color: COLORS.textMuted, fontSize: 13 }}>Bloque {l.block}</span><span style={{ fontSize: 13 }}><span style={{ color: COLORS.success }}>{l.ok} OK</span>{l.errors > 0 && <span style={{ color: COLORS.danger, marginLeft: 8 }}>{l.errors} err</span>}</span></div>)}</div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ADMINISTRACIÓN DE BODEGAS (crear / activar / desactivar)
// ═══════════════════════════════════════════════════════════════
function BodegasAdmin({ token }) {
  const [bodegas, setBodegas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null); // { tipo: "ok" | "error", texto }
  const [form, setForm] = useState({ id_bodega: "", descripcion: "", tipo: "tienda", ubicacion: "" });
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const cargar = useCallback(() => {
    setLoading(true);
    apiFetch("/bodegas", {}, token)
      .then(res => setBodegas(Array.isArray(res) ? res : []))
      .catch(e => setMsg({ tipo: "error", texto: e.message }))
      .finally(() => setLoading(false));
  }, [token]);
  useEffect(() => { cargar(); }, [cargar]);

  const crear = async () => {
    if (!form.id_bodega.trim() || !form.descripcion.trim()) {
      setMsg({ tipo: "error", texto: "ID y descripción son obligatorios" }); return;
    }
    setSaving(true); setMsg(null);
    try {
      await apiFetch("/bodegas", {
        method: "POST",
        body: JSON.stringify({ ...form, id_bodega: form.id_bodega.trim(), descripcion: form.descripcion.trim(), ubicacion: form.ubicacion.trim() || null }),
      }, token);
      setMsg({ tipo: "ok", texto: `Bodega ${form.id_bodega.trim()} creada` });
      setForm({ id_bodega: "", descripcion: "", tipo: "tienda", ubicacion: "" });
      cargar();
    } catch (e) { setMsg({ tipo: "error", texto: e.message }); }
    setSaving(false);
  };

  const toggle = async (b) => {
    const activar = !b.activo;
    if (!activar && !window.confirm(
      `¿Desactivar la bodega ${b.id_bodega} — ${b.descripcion}?\n\n` +
      "Dejará de considerarse en TODOS los cálculos: pronósticos, KPIs, " +
      "inventario óptimo, traslados y reportes BI.\n" +
      "Sus datos históricos se conservan y puede reactivarla cuando quiera."
    )) return;
    setTogglingId(b.id_bodega); setMsg(null);
    try {
      const res = await apiFetch(`/bodegas/${encodeURIComponent(b.id_bodega)}?activo=${activar}`, { method: "PUT" }, token);
      setMsg({ tipo: "ok", texto: res?.mensaje || "Bodega actualizada" });
      cargar();
    } catch (e) { setMsg({ tipo: "error", texto: e.message }); }
    setTogglingId(null);
  };

  const tipoLabel = { tienda: "Tienda", centro_distribucion: "Centro distribución", bodega: "Bodega" };
  const activas = bodegas.filter(b => b.activo).length;

  return (
    <div>
      {msg && (
        <div style={{ padding: "10px 16px", marginBottom: 16, borderRadius: 10, fontSize: 13, display: "flex", alignItems: "center", gap: 8,
          background: msg.tipo === "ok" ? COLORS.successSoft : COLORS.dangerSoft,
          color: msg.tipo === "ok" ? COLORS.success : COLORS.danger,
          border: `1px solid ${msg.tipo === "ok" ? COLORS.success : COLORS.danger}33` }}>
          {msg.tipo === "ok" ? <CheckCircle size={15} /> : <AlertTriangle size={15} />} {msg.texto}
        </div>
      )}

      {/* ── Crear bodega ── */}
      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <h3 style={{ color: COLORS.text, fontSize: 15, fontWeight: 600, margin: "0 0 16px", display: "flex", alignItems: "center", gap: 8 }}>
          <Building2 size={16} color={COLORS.primary} /> Nueva Bodega
        </h3>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div><label style={labelStyle}>ID *</label>
            <input value={form.id_bodega} onChange={e => setForm(f => ({ ...f, id_bodega: e.target.value }))} placeholder="BOD01" maxLength={50} style={{ ...inputStyle, width: 120 }} /></div>
          <div><label style={labelStyle}>Descripción *</label>
            <input value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} placeholder="Tienda Centro" maxLength={200} style={{ ...inputStyle, width: 220 }} /></div>
          <div><label style={labelStyle}>Tipo</label>
            <select value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))} style={{ ...inputStyle, width: 180 }}>
              <option value="tienda">Tienda</option>
              <option value="centro_distribucion">Centro distribución</option>
              <option value="bodega">Bodega</option>
            </select></div>
          <div><label style={labelStyle}>Ubicación</label>
            <input value={form.ubicacion} onChange={e => setForm(f => ({ ...f, ubicacion: e.target.value }))} placeholder="Opcional" maxLength={200} style={{ ...inputStyle, width: 200 }} /></div>
          <button onClick={crear} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>{saving ? "Creando..." : "Crear Bodega"}</button>
        </div>
      </div>

      {/* ── Listado ── */}
      <div style={{ marginBottom: 8, color: COLORS.textMuted, fontSize: 13 }}>
        {bodegas.length} bodegas · {activas} activas · {bodegas.length - activas} inactivas — las inactivas no participan en ningún cálculo
      </div>
      <div style={tableWrap}><div style={{ maxHeight: 500, overflowY: "auto" }}>
        {loading ? <div style={{ padding: 40, textAlign: "center", color: COLORS.textMuted }}>Cargando...</div> :
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr style={{ background: COLORS.surfaceHover, position: "sticky", top: 0 }}>
              {["ID", "Descripción", "Tipo", "Ubicación", "Estado", "Acción"].map(h => <th key={h} style={thStyle}>{h}</th>)}
            </tr></thead>
            <tbody>{bodegas.map(b => (
              <tr key={b.id_bodega} style={{ borderBottom: `1px solid ${COLORS.border}`, opacity: b.activo ? 1 : 0.55 }}>
                <td style={{ ...tdStyle, ...mono }}>{b.id_bodega}</td>
                <td style={tdStyle}>{b.descripcion}</td>
                <td style={tdStyle}>{tipoLabel[b.tipo] || b.tipo}</td>
                <td style={tdStyle}>{b.ubicacion || "—"}</td>
                <td style={tdStyle}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                    background: b.activo ? COLORS.successSoft : COLORS.dangerSoft,
                    color: b.activo ? COLORS.success : COLORS.danger }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: b.activo ? COLORS.success : COLORS.danger }} />
                    {b.activo ? "Activa" : "Inactiva"}
                  </span>
                </td>
                <td style={tdStyle}>
                  <button onClick={() => toggle(b)} disabled={togglingId === b.id_bodega}
                    style={{ ...(b.activo ? btnSmallDanger : btnSmallSuccess), opacity: togglingId === b.id_bodega ? 0.6 : 1 }}>
                    {togglingId === b.id_bodega ? "..." : b.activo ? "Desactivar" : "Activar"}
                  </button>
                </td>
              </tr>
            ))}</tbody>
          </table>}
      </div></div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ADMIN CLASIFICADORES (definir clasificadores y tipos)
// ═══════════════════════════════════════════════════════════════
function ClasificadoresAdmin({ token }) {
  const [clasificadores, setClasificadores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null); // { tipo: "ok" | "error", texto }
  const [form, setForm] = useState({ id_clasificador: "", nombre_clasificador: "", porcentaje_minimo: 10, principal: false });
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ nombre_clasificador: "", porcentaje_minimo: 0, principal: false });

  // detalle: tipos del clasificador seleccionado
  const [selected, setSelected] = useState(null); // objeto clasificador
  const [tipos, setTipos] = useState([]);
  const [tiposLoading, setTiposLoading] = useState(false);
  const [tipoForm, setTipoForm] = useState({ id_tipo: "", nombre_tipo: "" });
  const [tipoSaving, setTipoSaving] = useState(false);
  const [tipoTogglingId, setTipoTogglingId] = useState(null);

  const cargar = useCallback(() => {
    setLoading(true);
    apiFetch("/clasificadores?incluir_inactivos=true", {}, token)
      .then(res => setClasificadores(Array.isArray(res) ? res : []))
      .catch(e => setMsg({ tipo: "error", texto: e.message }))
      .finally(() => setLoading(false));
  }, [token]);
  useEffect(() => { cargar(); }, [cargar]);

  const cargarTipos = useCallback((clas) => {
    if (!clas) { setTipos([]); return; }
    setTiposLoading(true);
    apiFetch(`/clasificadores/${encodeURIComponent(clas)}/tipos?incluir_inactivos=true`, {}, token)
      .then(res => setTipos(Array.isArray(res) ? res : []))
      .catch(e => setMsg({ tipo: "error", texto: e.message }))
      .finally(() => setTiposLoading(false));
  }, [token]);
  useEffect(() => { cargarTipos(selected?.id_clasificador); }, [selected, cargarTipos]);

  const crear = async () => {
    const idNuevo = form.id_clasificador.trim();
    if (!idNuevo || !form.nombre_clasificador.trim()) {
      setMsg({ tipo: "error", texto: "ID y nombre son obligatorios" }); return;
    }
    if (clasificadores.some(c => c.id_clasificador === idNuevo)) {
      setMsg({ tipo: "error", texto: `Ya existe un clasificador con ID "${idNuevo}". Use Editar para modificarlo.` }); return;
    }
    setSaving(true); setMsg(null);
    try {
      await apiFetch("/clasificadores", {
        method: "POST",
        body: JSON.stringify({
          id_clasificador: form.id_clasificador.trim(),
          nombre_clasificador: form.nombre_clasificador.trim(),
          porcentaje_minimo: Number(form.porcentaje_minimo) || 0,
          principal: form.principal ? "S" : "N",
        }),
      }, token);
      setMsg({ tipo: "ok", texto: `Clasificador ${form.id_clasificador.trim()} guardado` });
      setForm({ id_clasificador: "", nombre_clasificador: "", porcentaje_minimo: 10, principal: false });
      cargar();
    } catch (e) { setMsg({ tipo: "error", texto: e.message }); }
    setSaving(false);
  };

  const startEdit = (c) => {
    setEditId(c.id_clasificador);
    setEditForm({
      nombre_clasificador: c.nombre_clasificador || "",
      porcentaje_minimo: c.porcentaje_minimo ?? 0,
      principal: c.principal === "S",
    });
  };

  const guardarEdit = async (c) => {
    setSaving(true); setMsg(null);
    try {
      const p = new URLSearchParams();
      p.set("nombre_clasificador", editForm.nombre_clasificador.trim());
      p.set("porcentaje_minimo", String(Number(editForm.porcentaje_minimo) || 0));
      p.set("principal", editForm.principal ? "S" : "N");
      await apiFetch(`/clasificadores/${encodeURIComponent(c.id_clasificador)}?${p}`, { method: "PUT" }, token);
      setMsg({ tipo: "ok", texto: "Clasificador actualizado" });
      setEditId(null);
      cargar();
    } catch (e) { setMsg({ tipo: "error", texto: e.message }); }
    setSaving(false);
  };

  const toggle = async (c) => {
    const activar = !c.activo;
    if (!activar && !window.confirm(
      `¿Desactivar el clasificador ${c.id_clasificador} — ${c.nombre_clasificador}?\n\n` +
      "Dejará de aparecer en filtros y análisis por clasificador.\n" +
      "Sus datos se conservan y puede reactivarlo cuando quiera."
    )) return;
    setTogglingId(c.id_clasificador); setMsg(null);
    try {
      if (activar) {
        await apiFetch(`/clasificadores/${encodeURIComponent(c.id_clasificador)}?activo=true`, { method: "PUT" }, token);
      } else {
        await apiFetch(`/clasificadores/${encodeURIComponent(c.id_clasificador)}`, { method: "DELETE" }, token);
      }
      setMsg({ tipo: "ok", texto: `Clasificador ${activar ? "activado" : "desactivado"}` });
      if (!activar && selected?.id_clasificador === c.id_clasificador) setSelected(null);
      cargar();
    } catch (e) { setMsg({ tipo: "error", texto: e.message }); }
    setTogglingId(null);
  };

  const crearTipo = async () => {
    if (!selected) return;
    if (!tipoForm.id_tipo.trim() || !tipoForm.nombre_tipo.trim()) {
      setMsg({ tipo: "error", texto: "ID y nombre del tipo son obligatorios" }); return;
    }
    setTipoSaving(true); setMsg(null);
    try {
      await apiFetch(`/clasificadores/${encodeURIComponent(selected.id_clasificador)}/tipos`, {
        method: "POST",
        body: JSON.stringify({ id_tipo: tipoForm.id_tipo.trim(), nombre_tipo: tipoForm.nombre_tipo.trim() }),
      }, token);
      setMsg({ tipo: "ok", texto: `Tipo ${tipoForm.id_tipo.trim()} guardado` });
      setTipoForm({ id_tipo: "", nombre_tipo: "" });
      cargarTipos(selected.id_clasificador);
      cargar();
    } catch (e) { setMsg({ tipo: "error", texto: e.message }); }
    setTipoSaving(false);
  };

  const toggleTipo = async (t) => {
    const activar = !t.activo;
    setTipoTogglingId(t.id_tipo); setMsg(null);
    try {
      if (activar) {
        await apiFetch(`/clasificadores/${encodeURIComponent(selected.id_clasificador)}/tipos/${encodeURIComponent(t.id_tipo)}?activo=true`, { method: "PUT" }, token);
      } else {
        await apiFetch(`/clasificadores/${encodeURIComponent(selected.id_clasificador)}/tipos/${encodeURIComponent(t.id_tipo)}`, { method: "DELETE" }, token);
      }
      setMsg({ tipo: "ok", texto: `Tipo ${activar ? "activado" : "desactivado"}` });
      cargarTipos(selected.id_clasificador);
      cargar();
    } catch (e) { setMsg({ tipo: "error", texto: e.message }); }
    setTipoTogglingId(null);
  };

  const activos = clasificadores.filter(c => c.activo).length;

  return (
    <div>
      {msg && (
        <div style={{ padding: "10px 16px", marginBottom: 16, borderRadius: 10, fontSize: 13, display: "flex", alignItems: "center", gap: 8,
          background: msg.tipo === "ok" ? COLORS.successSoft : COLORS.dangerSoft,
          color: msg.tipo === "ok" ? COLORS.success : COLORS.danger,
          border: `1px solid ${msg.tipo === "ok" ? COLORS.success : COLORS.danger}33` }}>
          {msg.tipo === "ok" ? <CheckCircle size={15} /> : <AlertTriangle size={15} />} {msg.texto}
        </div>
      )}

      {/* ── Crear clasificador ── */}
      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <h3 style={{ color: COLORS.text, fontSize: 15, fontWeight: 600, margin: "0 0 16px", display: "flex", alignItems: "center", gap: 8 }}>
          <Tag size={16} color={COLORS.primary} /> Nuevo Clasificador
        </h3>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div><label style={labelStyle}>ID *</label>
            <input value={form.id_clasificador} onChange={e => setForm(f => ({ ...f, id_clasificador: e.target.value }))} placeholder="MARCA" maxLength={50} style={{ ...inputStyle, width: 140 }} /></div>
          <div><label style={labelStyle}>Nombre *</label>
            <input value={form.nombre_clasificador} onChange={e => setForm(f => ({ ...f, nombre_clasificador: e.target.value }))} placeholder="Marca" maxLength={200} style={{ ...inputStyle, width: 220 }} /></div>
          <div><label style={labelStyle}>% mínimo</label>
            <input type="number" value={form.porcentaje_minimo} onChange={e => setForm(f => ({ ...f, porcentaje_minimo: e.target.value }))} min={0} max={100} step="0.01" style={{ ...inputStyle, width: 100 }} /></div>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: COLORS.textMuted, paddingBottom: 10 }}>
            <input type="checkbox" checked={form.principal} onChange={e => setForm(f => ({ ...f, principal: e.target.checked }))} /> Principal
          </label>
          <button onClick={crear} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>{saving ? "Guardando..." : "Crear Clasificador"}</button>
        </div>
      </div>

      {/* ── Listado ── */}
      <div style={{ marginBottom: 8, color: COLORS.textMuted, fontSize: 13 }}>
        {clasificadores.length} clasificadores · {activos} activos · {clasificadores.length - activos} inactivos — seleccione uno para administrar sus tipos
      </div>
      <div style={tableWrap}><div style={{ maxHeight: 400, overflowY: "auto" }}>
        {loading ? <div style={{ padding: 40, textAlign: "center", color: COLORS.textMuted }}>Cargando...</div> :
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr style={{ background: COLORS.surfaceHover, position: "sticky", top: 0 }}>
              {["ID", "Nombre", "% mín", "Principal", "Tipos", "Estado", "Acciones"].map(h => <th key={h} style={thStyle}>{h}</th>)}
            </tr></thead>
            <tbody>{clasificadores.map(c => {
              const isEditing = editId === c.id_clasificador;
              const isSelected = selected?.id_clasificador === c.id_clasificador;
              return (
              <tr key={c.id_clasificador} onClick={() => !isEditing && setSelected(c)}
                style={{ borderBottom: `1px solid ${COLORS.border}`, opacity: c.activo ? 1 : 0.55, cursor: "pointer",
                  background: isSelected ? COLORS.primarySoft : "transparent" }}>
                <td style={{ ...tdStyle, ...mono }}>{c.id_clasificador}</td>
                <td style={tdStyle}>{isEditing
                  ? <input value={editForm.nombre_clasificador} onClick={e => e.stopPropagation()} onChange={e => setEditForm(f => ({ ...f, nombre_clasificador: e.target.value }))} maxLength={200} style={{ ...inputStyle, width: 180 }} />
                  : c.nombre_clasificador}</td>
                <td style={tdStyle}>{isEditing
                  ? <input type="number" value={editForm.porcentaje_minimo} onClick={e => e.stopPropagation()} onChange={e => setEditForm(f => ({ ...f, porcentaje_minimo: e.target.value }))} min={0} max={100} step="0.01" style={{ ...inputStyle, width: 80 }} />
                  : `${c.porcentaje_minimo ?? 0}%`}</td>
                <td style={tdStyle}>{isEditing
                  ? <input type="checkbox" checked={editForm.principal && c.activo} disabled={!c.activo} title={!c.activo ? "Un clasificador inactivo no puede ser principal" : undefined} onClick={e => e.stopPropagation()} onChange={e => setEditForm(f => ({ ...f, principal: e.target.checked }))} />
                  : (c.principal === "S"
                    ? <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: COLORS.primarySoft, color: COLORS.primary }}>Principal</span>
                    : <span style={{ color: COLORS.textDim }}>—</span>)}</td>
                <td style={tdStyle}>{c.total_tipos}</td>
                <td style={tdStyle}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                    background: c.activo ? COLORS.successSoft : COLORS.dangerSoft,
                    color: c.activo ? COLORS.success : COLORS.danger }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.activo ? COLORS.success : COLORS.danger }} />
                    {c.activo ? "Activo" : "Inactivo"}
                  </span>
                </td>
                <td style={tdStyle} onClick={e => e.stopPropagation()}>
                  <div style={{ display: "flex", gap: 6 }}>
                    {isEditing ? <>
                      <button onClick={() => guardarEdit(c)} disabled={saving} style={btnSmallSuccess}>Guardar</button>
                      <button onClick={() => setEditId(null)} style={btnSmallInfo}>Cancelar</button>
                    </> : <>
                      <button onClick={() => startEdit(c)} style={btnSmallInfo}>Editar</button>
                      <button onClick={() => toggle(c)} disabled={togglingId === c.id_clasificador}
                        style={{ ...(c.activo ? btnSmallDanger : btnSmallSuccess), opacity: togglingId === c.id_clasificador ? 0.6 : 1 }}>
                        {togglingId === c.id_clasificador ? "..." : c.activo ? "Desactivar" : "Activar"}
                      </button>
                    </>}
                  </div>
                </td>
              </tr>
            ); })}</tbody>
          </table>}
      </div></div>

      {/* ── Detalle: tipos del clasificador seleccionado ── */}
      {selected && (
        <div style={{ ...cardStyle, marginTop: 20 }}>
          <h3 style={{ color: COLORS.text, fontSize: 15, fontWeight: 600, margin: "0 0 16px", display: "flex", alignItems: "center", gap: 8 }}>
            <Layers size={16} color={COLORS.accent} /> Tipos de "{selected.nombre_clasificador}"{selected.total_tipos > tipos.length ? ` (primeros ${tipos.length} de ${selected.total_tipos})` : ""}
          </h3>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 16 }}>
            <div><label style={labelStyle}>ID tipo *</label>
              <input value={tipoForm.id_tipo} onChange={e => setTipoForm(f => ({ ...f, id_tipo: e.target.value }))} placeholder="NIKE" maxLength={50} style={{ ...inputStyle, width: 140 }} /></div>
            <div><label style={labelStyle}>Nombre tipo *</label>
              <input value={tipoForm.nombre_tipo} onChange={e => setTipoForm(f => ({ ...f, nombre_tipo: e.target.value }))} placeholder="Nike" maxLength={200} style={{ ...inputStyle, width: 220 }} /></div>
            <button onClick={crearTipo} disabled={tipoSaving} style={{ ...btnPrimary, opacity: tipoSaving ? 0.6 : 1 }}>{tipoSaving ? "Guardando..." : "Agregar Tipo"}</button>
          </div>
          <div style={tableWrap}><div style={{ maxHeight: 300, overflowY: "auto" }}>
            {tiposLoading ? <div style={{ padding: 30, textAlign: "center", color: COLORS.textMuted }}>Cargando...</div> :
              tipos.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: COLORS.textMuted }}>Sin tipos definidos</div> :
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr style={{ background: COLORS.surfaceHover, position: "sticky", top: 0 }}>
                  {["ID", "Nombre", "Estado", "Acción"].map(h => <th key={h} style={thStyle}>{h}</th>)}
                </tr></thead>
                <tbody>{tipos.map(t => (
                  <tr key={t.id_tipo} style={{ borderBottom: `1px solid ${COLORS.border}`, opacity: t.activo ? 1 : 0.55 }}>
                    <td style={{ ...tdStyle, ...mono }}>{t.id_tipo}</td>
                    <td style={tdStyle}>{t.nombre_tipo}</td>
                    <td style={tdStyle}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                        background: t.activo ? COLORS.successSoft : COLORS.dangerSoft,
                        color: t.activo ? COLORS.success : COLORS.danger }}>
                        {t.activo ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <button onClick={() => toggleTipo(t)} disabled={tipoTogglingId === t.id_tipo}
                        style={{ ...(t.activo ? btnSmallDanger : btnSmallSuccess), opacity: tipoTogglingId === t.id_tipo ? 0.6 : 1 }}>
                        {tipoTogglingId === t.id_tipo ? "..." : t.activo ? "Desactivar" : "Activar"}
                      </button>
                    </td>
                  </tr>
                ))}</tbody>
              </table>}
          </div></div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CLASIFICAR ARTÍCULOS (asignación artículo -> tipo de clasificador)
// ═══════════════════════════════════════════════════════════════
const MAX_TIPOS_ASIGNABLE = 2000;

function ClasificarArticulosAdmin({ token }) {
  const [clasificadores, setClasificadores] = useState([]);
  const [loadingClas, setLoadingClas] = useState(true);
  const [msg, setMsg] = useState(null); // { tipo: "ok" | "error", texto }

  const [query, setQuery] = useState("");
  const [resultados, setResultados] = useState(null); // { total, data } | null
  const [buscando, setBuscando] = useState(false);

  const [selected, setSelected] = useState(null); // { id_articulo, descripcion }
  const [clasificaciones, setClasificaciones] = useState([]);
  const [loadingClasif, setLoadingClasif] = useState(false);

  const [formClas, setFormClas] = useState(""); // id_clasificador
  const [formTipo, setFormTipo] = useState(""); // id_tipo
  const [asignando, setAsignando] = useState(false);
  const [quitando, setQuitando] = useState(null); // "id_clasificador:id_tipo" en curso

  // clasificadores activos con sus tipos anidados (para los dropdowns)
  useEffect(() => {
    setLoadingClas(true);
    apiFetch("/clasificadores", {}, token)
      .then(res => setClasificadores(Array.isArray(res) ? res : []))
      .catch(e => setMsg({ tipo: "error", texto: e.message }))
      .finally(() => setLoadingClas(false));
  }, [token]);

  const buscar = async () => {
    const q = query.trim();
    if (!q) { setResultados(null); return; }
    setBuscando(true); setMsg(null);
    try {
      const res = await apiFetch(`/articulos?buscar=${encodeURIComponent(q)}&size=20`, {}, token);
      setResultados({ total: res?.total || 0, data: res?.data || [] });
    } catch (e) { setMsg({ tipo: "error", texto: e.message }); }
    setBuscando(false);
  };

  const cargarClasificaciones = useCallback((idArt) => {
    if (!idArt) { setClasificaciones([]); return; }
    setLoadingClasif(true);
    apiFetch(`/articulos/${encodeURIComponent(idArt)}/clasificaciones`, {}, token)
      .then(res => setClasificaciones(Array.isArray(res) ? res : []))
      .catch(e => setMsg({ tipo: "error", texto: e.message }))
      .finally(() => setLoadingClasif(false));
  }, [token]);

  const seleccionar = (art) => {
    setSelected(art);
    setFormClas(""); setFormTipo(""); setMsg(null);
    cargarClasificaciones(art.id_articulo);
  };

  const asignar = async () => {
    if (!formClas || !formTipo) {
      setMsg({ tipo: "error", texto: "Elija clasificador y tipo" }); return;
    }
    setAsignando(true); setMsg(null);
    try {
      await apiFetch("/articulos/clasificar", {
        method: "POST",
        body: JSON.stringify({ id_articulo: selected.id_articulo, id_clasificador: formClas, id_tipo: formTipo }),
      }, token);
      setMsg({ tipo: "ok", texto: "Artículo clasificado" });
      setFormTipo("");
      cargarClasificaciones(selected.id_articulo);
    } catch (e) { setMsg({ tipo: "error", texto: e.message }); }
    setAsignando(false);
  };

  const quitar = async (c) => {
    const key = `${c.id_clasificador}:${c.id_tipo}`;
    if (quitando) return;
    setQuitando(key); setMsg(null);
    try {
      await apiFetch(`/articulos/${encodeURIComponent(selected.id_articulo)}/clasificar/${encodeURIComponent(c.id_clasificador)}/${encodeURIComponent(c.id_tipo)}`, { method: "DELETE" }, token);
      setMsg({ tipo: "ok", texto: "Clasificación eliminada" });
      cargarClasificaciones(selected.id_articulo);
    } catch (e) { setMsg({ tipo: "error", texto: e.message }); }
    setQuitando(null);
  };

  const clasSel = clasificadores.find(c => c.id_clasificador === formClas);
  const tiposDisponibles = clasSel?.tipos || [];

  return (
    <div>
      {msg && (
        <div style={{ padding: "10px 16px", marginBottom: 16, borderRadius: 10, fontSize: 13, display: "flex", alignItems: "center", gap: 8,
          background: msg.tipo === "ok" ? COLORS.successSoft : COLORS.dangerSoft,
          color: msg.tipo === "ok" ? COLORS.success : COLORS.danger,
          border: `1px solid ${msg.tipo === "ok" ? COLORS.success : COLORS.danger}33` }}>
          {msg.tipo === "ok" ? <CheckCircle size={15} /> : <AlertTriangle size={15} />} {msg.texto}
        </div>
      )}

      {/* ── Búsqueda de artículo ── */}
      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <h3 style={{ color: COLORS.text, fontSize: 15, fontWeight: 600, margin: "0 0 16px", display: "flex", alignItems: "center", gap: 8 }}>
          <Search size={16} color={COLORS.primary} /> Buscar Artículo
        </h3>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>ID o descripción</label>
            <input value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => { if (e.key === "Enter") buscar(); }} placeholder="Ej: 1001 o 'tornillo'" style={inputStyle} />
          </div>
          <button onClick={buscar} disabled={buscando} style={{ ...btnPrimary, opacity: buscando ? 0.6 : 1 }}>{buscando ? "Buscando..." : "Buscar"}</button>
        </div>
        {resultados && (
          <div style={{ marginTop: 12 }}>
            <div style={{ color: COLORS.textMuted, fontSize: 12, marginBottom: 6 }}>
              {resultados.total === 0 ? "Sin resultados" : `Mostrando ${resultados.data.length} de ${resultados.total}`}
              {resultados.total > resultados.data.length ? " — refine la búsqueda para ver más" : ""}
            </div>
            {resultados.data.length > 0 && (
              <div style={tableWrap}><div style={{ maxHeight: 240, overflowY: "auto" }}>
                {resultados.data.map(a => (
                  <div key={a.id_articulo} onClick={() => seleccionar(a)}
                    style={{ padding: "8px 14px", borderBottom: `1px solid ${COLORS.border}`, cursor: "pointer",
                      background: selected?.id_articulo === a.id_articulo ? COLORS.primarySoft : "transparent",
                      display: "flex", gap: 10 }}>
                    <span style={{ ...mono, color: COLORS.textSecond, minWidth: 90 }}>{a.id_articulo}</span>
                    <span style={{ color: COLORS.text }}>{a.descripcion}</span>
                  </div>
                ))}
              </div></div>
            )}
          </div>
        )}
      </div>

      {/* ── Panel del artículo seleccionado ── */}
      {selected && (
        <div style={cardStyle}>
          <h3 style={{ color: COLORS.text, fontSize: 15, fontWeight: 600, margin: "0 0 4px", display: "flex", alignItems: "center", gap: 8 }}>
            <Package size={16} color={COLORS.accent} /> {selected.id_articulo}
          </h3>
          <div style={{ color: COLORS.textMuted, fontSize: 13, marginBottom: 16 }}>{selected.descripcion}</div>

          {/* Asignar */}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Clasificador</label>
              <select value={formClas} onChange={e => { setFormClas(e.target.value); setFormTipo(""); }} disabled={loadingClas} style={{ ...inputStyle, width: 240 }}>
                <option value="">{loadingClas ? "Cargando..." : "— Seleccione —"}</option>
                {clasificadores.map(c => {
                  const asignable = (c.total_tipos || 0) <= MAX_TIPOS_ASIGNABLE;
                  return (
                    <option key={c.id_clasificador} value={c.id_clasificador} disabled={!asignable}>
                      {(c.nombre_clasificador || c.id_clasificador)}{asignable ? "" : " (solo importación CSV)"}
                    </option>
                  );
                })}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Tipo</label>
              <select value={formTipo} onChange={e => setFormTipo(e.target.value)} disabled={!formClas} style={{ ...inputStyle, width: 260 }}>
                <option value="">{formClas ? "— Seleccione —" : "Elija clasificador primero"}</option>
                {tiposDisponibles.map(t => (
                  <option key={t.id_tipo} value={t.id_tipo}>{t.nombre_tipo || t.id_tipo}</option>
                ))}
              </select>
            </div>
            <button onClick={asignar} disabled={asignando} style={{ ...btnPrimary, opacity: asignando ? 0.6 : 1 }}>{asignando ? "Asignando..." : "Asignar"}</button>
          </div>

          {/* Clasificaciones actuales */}
          <div style={{ marginBottom: 8, color: COLORS.textMuted, fontSize: 13 }}>Clasificaciones actuales</div>
          <div style={tableWrap}><div style={{ maxHeight: 300, overflowY: "auto" }}>
            {loadingClasif ? <div style={{ padding: 30, textAlign: "center", color: COLORS.textMuted }}>Cargando...</div> :
              clasificaciones.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: COLORS.textMuted }}>Sin clasificaciones</div> :
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr style={{ background: COLORS.surfaceHover, position: "sticky", top: 0 }}>
                  {["Clasificador", "Tipo", "Acción"].map(h => <th key={h} style={thStyle}>{h}</th>)}
                </tr></thead>
                <tbody>{clasificaciones.map(c => (
                  <tr key={`${c.id_clasificador}:${c.id_tipo}`} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                    <td style={tdStyle}>{c.nombre_clasificador || c.id_clasificador}</td>
                    <td style={tdStyle}>{c.nombre_tipo || c.id_tipo}</td>
                    <td style={tdStyle}><button onClick={() => quitar(c)} disabled={quitando === `${c.id_clasificador}:${c.id_tipo}`} style={{ ...btnSmallDanger, opacity: quitando === `${c.id_clasificador}:${c.id_tipo}` ? 0.6 : 1 }}>{quitando === `${c.id_clasificador}:${c.id_tipo}` ? "..." : "Quitar"}</button></td>
                  </tr>
                ))}</tbody>
              </table>}
          </div></div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAESTROS
// ═══════════════════════════════════════════════════════════════
function MaestrosPage({ token }) {
  const [tab, setTab] = useState("bodegas"); const [data, setData] = useState([]); const [loading, setLoading] = useState(false);
  useEffect(() => { if (tab === "bodegas" || tab === "def_clasificadores" || tab === "clasificar_articulos") return; // estas pestañas tienen su propio componente
    setLoading(true); const ep = { articulos: "/articulos", clasificadores: "/clasificadores", preferencias: "/preferencias-traslado", usuarios: "/usuarios" };
    apiFetch(ep[tab] || "/articulos", {}, token).then(res => setData(res?.data || res || [])).catch(console.error).finally(() => setLoading(false)); }, [tab, token]);
  const tabs = [{ id: "bodegas", icon: Building2, label: "Bodegas" }, { id: "def_clasificadores", icon: Tag, label: "Definir Clasificadores" }, { id: "clasificar_articulos", icon: Package, label: "Clasificar Artículos" }, { id: "articulos", icon: Package, label: "Artículos" }, { id: "clasificadores", icon: Tag, label: "Clasificadores" }, { id: "preferencias", icon: Layers, label: "Pref. Traslado" }, { id: "usuarios", icon: Users, label: "Usuarios" }];
  return (
    <div><PageHeader title="Datos Maestros" subtitle="Administración de catálogos" />
      <div style={{ display: "flex", gap: 4, marginBottom: 20, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 4 }}>{tabs.map(t => { const I = t.icon; return <button key={t.id} onClick={() => setTab(t.id)} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 16px", border: "none", borderRadius: 8, cursor: "pointer", background: tab === t.id ? COLORS.primarySoft : "transparent", color: tab === t.id ? COLORS.primary : COLORS.textMuted, fontSize: 13, fontWeight: tab === t.id ? 600 : 400 }}><I size={15} /> {t.label}</button>; })}</div>
      {tab === "bodegas" ? <BodegasAdmin token={token} /> :
      tab === "def_clasificadores" ? <ClasificadoresAdmin token={token} /> :
      tab === "clasificar_articulos" ? <ClasificarArticulosAdmin token={token} /> :
      <div style={tableWrap}><div style={{ maxHeight: 500, overflowY: "auto" }}>{loading ? <div style={{ padding: 40, textAlign: "center", color: COLORS.textMuted }}>Cargando...</div> :
        <table style={{ width: "100%", borderCollapse: "collapse" }}><thead><tr style={{ background: COLORS.surfaceHover, position: "sticky", top: 0 }}>{data.length > 0 && Object.keys(data[0]).filter(k => !k.startsWith("id_empresa") && k !== "password").slice(0, 6).map(h => <th key={h} style={thStyle}>{h.replace(/_/g, " ")}</th>)}</tr></thead>
          <tbody>{data.map((r, i) => <tr key={i} style={{ borderBottom: `1px solid ${COLORS.border}` }}>{Object.entries(r).filter(([k]) => !k.startsWith("id_empresa") && k !== "password").slice(0, 6).map(([k, v], j) => <td key={j} style={tdStyle}>{typeof v === "object" ? JSON.stringify(v)?.slice(0, 50) : String(v ?? "")}</td>)}</tr>)}</tbody>
        </table>}</div></div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CALIDAD DE DATOS
// ═══════════════════════════════════════════════════════════════
function CalidadDatosPage({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const loadData = async () => {
    setLoading(true);
    try { setData(await apiFetch("/data-quality", {}, token)); }
    catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { loadData(); }, [token]);

  const alertIcon = { critico: AlertTriangle, advertencia: Clock, info: CheckCircle };
  const alertColor = { critico: COLORS.danger, advertencia: COLORS.warning, info: COLORS.success };
  const alertBg = { critico: COLORS.dangerSoft, advertencia: COLORS.warningSoft, info: COLORS.successSoft };

  if (loading) return <><PageHeader title="Calidad de Datos" subtitle="Diagnóstico del estado de los datos cargados" /><LoadingSpinner /></>;
  if (!data) return <><PageHeader title="Calidad de Datos" subtitle="Diagnóstico del estado de los datos cargados" /><EmptyState message="No se pudo cargar el diagnóstico" /></>;

  const m = data.metricas;
  return (
    <div>
      <PageHeader title="Calidad de Datos" subtitle="Diagnóstico del estado de los datos cargados" action={
        <button onClick={loadData} style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: 6 }}><RefreshCw size={14} /> Actualizar</button>
      } />

      {/* ── Resumen de alertas ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
        <MetricCard icon={Activity} label="Total Alertas" value={data.total_alertas} color={data.criticas > 0 ? COLORS.danger : COLORS.success} />
        <MetricCard icon={AlertTriangle} label="Críticas" value={data.criticas} color={COLORS.danger} />
        <MetricCard icon={Clock} label="Advertencias" value={data.advertencias} color={COLORS.warning} />
        <MetricCard icon={CheckCircle} label="Informativas" value={data.info} color={COLORS.accent} />
      </div>

      {/* ── Alertas detalladas ── */}
      {data.alertas.length > 0 && (
        <div style={{ ...cardStyle, marginBottom: 24 }}>
          <h3 style={{ color: COLORS.text, fontSize: 16, fontWeight: 600, margin: "0 0 16px", display: "flex", alignItems: "center", gap: 8 }}><AlertTriangle size={18} color={COLORS.warning} /> Alertas Detectadas</h3>
          {data.alertas.map((a, i) => {
            const Icon = alertIcon[a.tipo] || CheckCircle;
            return (
              <div key={i} style={{ padding: "12px 16px", marginBottom: 8, background: alertBg[a.tipo], border: `1px solid ${alertColor[a.tipo]}33`, borderRadius: 10, borderLeft: `4px solid ${alertColor[a.tipo]}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <Icon size={16} color={alertColor[a.tipo]} />
                  <span style={{ color: alertColor[a.tipo], fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}>{a.categoria}</span>
                </div>
                <div style={{ color: COLORS.text, fontSize: 14, fontWeight: 600 }}>{a.mensaje}</div>
                <div style={{ color: COLORS.textDim, fontSize: 12, marginTop: 2 }}>{a.detalle}</div>
              </div>
            );
          })}
        </div>
      )}
      {data.alertas.length === 0 && (
        <div style={{ ...cardStyle, marginBottom: 24, textAlign: "center", padding: 30 }}>
          <CheckCircle size={40} color={COLORS.success} style={{ marginBottom: 12 }} />
          <h3 style={{ color: COLORS.success, fontSize: 16, fontWeight: 600, margin: "0 0 4px" }}>Sin alertas</h3>
          <p style={{ color: COLORS.textDim, fontSize: 13, margin: 0 }}>Los datos se ven correctos. No se detectaron problemas.</p>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
        {/* ── Frescura de datos ── */}
        <div style={cardStyle}>
          <h3 style={{ color: COLORS.text, fontSize: 15, fontWeight: 600, margin: "0 0 16px", display: "flex", alignItems: "center", gap: 8 }}><Clock size={16} color={COLORS.primary} /> Frescura de Datos</h3>
          {m.frescura && Object.entries(m.frescura).map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${COLORS.border}` }}>
              <span style={{ color: COLORS.textMuted, fontSize: 13 }}>{k.replace(/_/g, " ").replace("ultima", "Última").replace("ultimo", "Último")}</span>
              <span style={{ ...mono, fontSize: 13, color: v ? COLORS.text : COLORS.danger }}>{v ? v.slice(0, 10) : "Sin datos"}</span>
            </div>
          ))}
        </div>

        {/* ── Estadísticas de ventas ── */}
        <div style={cardStyle}>
          <h3 style={{ color: COLORS.text, fontSize: 15, fontWeight: 600, margin: "0 0 16px", display: "flex", alignItems: "center", gap: 8 }}><TrendingUp size={16} color={COLORS.accent} /> Ventas Cargadas</h3>
          {m.ventas && Object.entries(m.ventas).filter(([k]) => !["desde", "hasta"].includes(k)).map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${COLORS.border}` }}>
              <span style={{ color: COLORS.textMuted, fontSize: 13 }}>{k.replace(/_/g, " ")}</span>
              <span style={{ ...mono, fontSize: 13, color: k === "devoluciones" && v > 0 ? COLORS.warning : COLORS.text }}>{typeof v === "number" ? v.toLocaleString() : v || "—"}</span>
            </div>
          ))}
          {m.ventas && <div style={{ marginTop: 8, fontSize: 12, color: COLORS.textDim }}>Período: {m.ventas.desde?.slice(0, 10)} — {m.ventas.hasta?.slice(0, 10)}</div>}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
        {/* ── Consistencia ── */}
        <div style={cardStyle}>
          <h3 style={{ color: COLORS.text, fontSize: 15, fontWeight: 600, margin: "0 0 16px", display: "flex", alignItems: "center", gap: 8 }}><Layers size={16} color={COLORS.warning} /> Consistencia</h3>
          {m.consistencia && Object.entries(m.consistencia).map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${COLORS.border}` }}>
              <span style={{ color: COLORS.textMuted, fontSize: 13 }}>{k.replace(/_/g, " ").replace("arts", "Artículos")}</span>
              <span style={{ ...mono, fontSize: 13, color: v > 0 && k.includes("sin") ? COLORS.warning : COLORS.text }}>{v.toLocaleString()}</span>
            </div>
          ))}
        </div>

        {/* ── Pronósticos ── */}
        <div style={cardStyle}>
          <h3 style={{ color: COLORS.text, fontSize: 15, fontWeight: 600, margin: "0 0 16px", display: "flex", alignItems: "center", gap: 8 }}><BarChart3 size={16} color={COLORS.primary} /> Pronósticos</h3>
          {(m.pronosticos || []).map((p, i) => (
            <div key={i} style={{ padding: "10px 12px", marginBottom: 8, background: COLORS.surfaceHover, borderRadius: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: COLORS.accent, fontSize: 13, fontWeight: 600, ...mono }}>{p.modelo}</span>
                <span style={{ color: COLORS.textDim, fontSize: 12 }}>{p.skus} SKUs</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                <span style={{ color: COLORS.textMuted, fontSize: 12 }}>Confianza prom: {p.confianza_promedio}%</span>
                <span style={{ color: p.en_cero > 0 ? COLORS.danger : COLORS.success, fontSize: 12, fontWeight: 600 }}>{p.en_cero} en cero</span>
              </div>
            </div>
          ))}
          {(m.pronosticos || []).length === 0 && <p style={{ color: COLORS.textDim, fontSize: 13 }}>Sin pronósticos generados. Ejecute desde Pronósticos.</p>}
        </div>
      </div>

      {/* ── Últimas cargas ── */}
      {m.ultimas_cargas && m.ultimas_cargas.length > 0 && (
        <div style={cardStyle}>
          <h3 style={{ color: COLORS.text, fontSize: 15, fontWeight: 600, margin: "0 0 16px", display: "flex", alignItems: "center", gap: 8 }}><Upload size={16} color={COLORS.primary} /> Últimas Cargas</h3>
          <div style={{ maxHeight: 250, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr style={{ background: COLORS.surfaceHover }}>{["Tipo", "Estado", "Total", "OK", "Errores", "Fecha"].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>{m.ultimas_cargas.map((c, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{c.tipo_dato}</td>
                  <td style={tdStyle}><StatusBadge status={c.estado} /></td>
                  <td style={{ ...tdStyle, ...mono }}>{c.total_registros}</td>
                  <td style={{ ...tdStyle, ...mono, color: COLORS.success }}>{c.registros_ok}</td>
                  <td style={{ ...tdStyle, ...mono, color: c.registros_error > 0 ? COLORS.danger : COLORS.textDim }}>{c.registros_error}</td>
                  <td style={{ ...tdStyle, ...mono, fontSize: 12 }}>{c.created_at?.slice(0, 16)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CONFIGURACIÓN
// ═══════════════════════════════════════════════════════════════
function ConfigPage({ token }) {
  const { setSimbolo } = useMoneda();
  const [empresa, setEmpresa] = useState(null); const [form, setForm] = useState({}); const [saving, setSaving] = useState(false); const [msg, setMsg] = useState("");
  useEffect(() => { apiFetch("/empresa", {}, token).then(e => { setEmpresa(e); setForm(e); }).catch(console.error); }, [token]);
  const save = async () => { setSaving(true); try { const nuevoSimbolo = (form.simbolo_moneda || "").trim() || SIMBOLO_MONEDA_DEFAULT; await apiFetch("/empresa", { method: "PUT", body: JSON.stringify({ dias_permanencia_minima: Number(form.dias_permanencia_minima), dias_abastecimiento: Number(form.dias_abastecimiento), porcentaje_minimo_bodega: Number(form.porcentaje_minimo_bodega), tiempo_promedio_traslado: Number(form.tiempo_promedio_traslado), factor_vencimiento_traslado: Number(form.factor_vencimiento_traslado || 2), simbolo_moneda: nuevoSimbolo }) }, token); setSimbolo(nuevoSimbolo); setMsg("Guardado"); setTimeout(() => setMsg(""), 3000); } catch (e) { setMsg("Error: " + e.message); } setSaving(false); };
  if (!empresa) return <LoadingSpinner />;
  return (
    <div><PageHeader title="Configuración" subtitle="Parámetros operativos de la empresa" />
      <div style={{ maxWidth: 600, ...cardStyle }}><h3 style={{ color: COLORS.text, fontSize: 18, fontWeight: 600, margin: "0 0 24px" }}>{empresa.nombre}</h3>
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div><label style={labelStyle}>Días permanencia mínima (xx)</label><p style={{ color: COLORS.textDim, fontSize: 12, margin: "2px 0 6px" }}>Días antes de poder trasladar</p><input type="number" value={form.dias_permanencia_minima || ""} onChange={e => setForm(p => ({ ...p, dias_permanencia_minima: e.target.value }))} style={inputStyle} /></div>
          <div><label style={labelStyle}>Días abastecimiento (yy)</label><p style={{ color: COLORS.textDim, fontSize: 12, margin: "2px 0 6px" }}>Días de cobertura objetivo</p><input type="number" value={form.dias_abastecimiento || ""} onChange={e => setForm(p => ({ ...p, dias_abastecimiento: e.target.value }))} style={inputStyle} /></div>
          <div><label style={labelStyle}>% mínimo en bodega</label><p style={{ color: COLORS.textDim, fontSize: 12, margin: "2px 0 6px" }}>Porcentaje que no se puede trasladar</p><input type="number" step="0.1" value={form.porcentaje_minimo_bodega || ""} onChange={e => setForm(p => ({ ...p, porcentaje_minimo_bodega: e.target.value }))} style={inputStyle} /></div>
          <div><label style={labelStyle}>Tiempo promedio traslado (días)</label><p style={{ color: COLORS.textDim, fontSize: 12, margin: "2px 0 6px" }}>Días que tarda un traslado. Artículos aprobados se restan del stock disponible hasta ser recibidos</p><input type="number" value={form.tiempo_promedio_traslado || ""} onChange={e => setForm(p => ({ ...p, tiempo_promedio_traslado: e.target.value }))} style={inputStyle} /></div>
          <div><label style={labelStyle}>Factor vencimiento traslado</label><p style={{ color: COLORS.textDim, fontSize: 12, margin: "2px 0 6px" }}>Multiplicador × tiempo promedio. Ejemplo: si es 2 y el promedio es 8 días, a los 16 días se marca como "recibido por tiempo"</p><input type="number" step="0.5" value={form.factor_vencimiento_traslado || "2"} onChange={e => setForm(p => ({ ...p, factor_vencimiento_traslado: e.target.value }))} style={inputStyle} /></div>
          <div><label style={labelStyle}>Símbolo de moneda</label><p style={{ color: COLORS.textDim, fontSize: 12, margin: "2px 0 6px" }}>Se antepone a los montos en pantallas y reportes. Ejemplo: {fmtMoneda(1234567, (form.simbolo_moneda || "").trim() || SIMBOLO_MONEDA_DEFAULT)}</p><input type="text" maxLength={5} value={form.simbolo_moneda ?? ""} onChange={e => setForm(p => ({ ...p, simbolo_moneda: e.target.value }))} style={{ ...inputStyle, width: 120 }} /></div>
        </div>
        <button onClick={save} disabled={saving} style={{ ...btnPrimary, marginTop: 24 }}>{saving ? "Guardando..." : "Guardar"}</button>
        {msg && <div style={{ marginTop: 12, padding: "10px 16px", borderRadius: 8, background: msg.startsWith("Error") ? COLORS.dangerSoft : COLORS.successSoft, border: `1px solid ${msg.startsWith("Error") ? "#FECACA" : "#A7F3D0"}`, color: msg.startsWith("Error") ? COLORS.danger : COLORS.success, fontSize: 13 }}>{msg}</div>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════════════════════════
export default function App() {
  const [auth, setAuth] = useState(null);
  const [page, setPage] = useState("dashboard");
  const token = auth?.access_token;
  const user = auth?.user;
  const PAGES = useMemo(() => (!token ? {} : {
    dashboard: <DashboardPage token={token} />, inventario: <InventarioPage token={token} />, clasificadores: <ClasificadoresPage token={token} />,
    traslados: <TrasladosPage token={token} />, traslados_multi: <TrasladosMultiClasPage token={token} />, traslados_multi_saldo: <TrasladosMultiClasSaldoPage token={token} />, traslados_saldo: <TrasladosPorSaldoPage token={token} />, inventario_clas: <InventarioClasPage token={token} />, rotacion: <RotacionPage token={token} />, pronosticos: <PronosticosPage token={token} />,
    compras: <ComprasPage token={token} />, upload: <UploadPage token={token} />, maestros: <MaestrosPage token={token} />, calidad: <CalidadDatosPage token={token} />, config: <ConfigPage token={token} />,
    bi_kpis: <KpisPage token={token} />, bi_reportes: <ReportBuilderPage token={token} />,
    bi_sin_mov: <SinMovimientoPage token={token} />,
  }), [token]);
  const [aviso, setAviso] = useState("");
  useEffect(() => {
    const expirada = () => { setAuth(null); setPage("dashboard"); setAviso("Su sesión expiró. Ingrese de nuevo."); };
    window.addEventListener(EVENTO_SESION_EXPIRADA, expirada);
    return () => window.removeEventListener(EVENTO_SESION_EXPIRADA, expirada);
  }, []);
  if (!auth) return <LoginPage aviso={aviso} onLogin={a => { setAviso(""); setAuth(a); }} />;
  return (
    <MonedaProvider token={token}>
      <div style={{ display: "flex", minHeight: "100vh", background: COLORS.bg, fontFamily: "'DM Sans', -apple-system, sans-serif" }}>
        <Sidebar active={page} onNavigate={setPage} user={user} onLogout={() => { setAuth(null); setPage("dashboard"); }} />
        <main style={{ marginLeft: 220, flex: 1, padding: 32, maxWidth: "calc(100vw - 220px)" }}>{PAGES[page] || <DashboardPage token={token} />}</main>
      </div>
    </MonedaProvider>
  );
}
